import { ExpandCircleDownOutlined } from "@mui/icons-material";
import {
  Skeleton,
  Tooltip,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormControl,
  IconButton,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  LinearProgress,
} from "@mui/material";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import React, { useEffect, useState, useRef } from "react";
import "../../../style/style.css";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { logout } from "../../../Redux/authSlice";
import { endLoading, startLoading } from "../../../Redux/loadingSlice";
import showPremiumAlert, { calculateCryptoChange, getCustomerInfoFromLocalStorage, getDateFromTimestamp, getUserFromLocalStorage, getWeekdayFromTimestamp } from "../../../utils/utils";
import { Constant } from "../../../commom/Constants";
import { UrlConstant } from "../../../commom/UrlConstant";
import Axios from "../../../config/axios";
import Watchlistcard from "../Cards/watchlistcard";
import BoosterLoanCard from "../Cards/BoosterLoancard";
import BoosterLoanSkeleton from "../Cards/BoosterLoanSkeleton";
import moment from "moment";
import LoadingCards from "./LoadingCards";
import RightSideSection from "../SharedComponent/RightSideSection";
import axios from "axios";
import LinechartMini from "../charts/LinechartMini";
import LoadingMiniCharts from "./LoadingMiniCharts";
import SumSubKYC from "./Crypto/SumSubKYC";
import { Crypto } from '../../../types/crypto';
import background from '../../../style/images/crypto-background.png';
import ThunderIconCirlcle from "../SvgIcons/ThunderIconCirlcle";
import OnboardingStatusBar from "../SharedComponent/OnboardingStatusBar";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { EventNote } from "@mui/icons-material";
import { extractQuoteWithFallback } from "../../../helpers/cryptoQuoteHelper";
import { usePriceFormatter } from "../../../hooks";
import { formatPercentage } from "../../../utils/formatting";

const BAR_CHART_KEY = "0J7lfEpAefOuHSE6uSHdiW_5N0sThxfa";

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
  },
  components: {
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: '#2A2A2A', // Base color
        },
        pulse: {
          '&::after': {
            background: 'linear-gradient(90deg, #2A2A2A, #3C3C3C)', // Highlight animation
          },
        },
      },
    },
  },
});

function CryptoMarketSection() {
  const [cryptos, setCryptos] = React.useState<Crypto[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const showOnlyLoanEligible = useAppSelector((state) => state.cryptoFilter.showOnlyLoanEligible);
  const currencyInfo = useAppSelector((state) => state.currency);
  const currencyKey = currencyInfo.effectiveCurrency || 'USD';
  const formatter = usePriceFormatter()

  const filteredCryptos = React.useMemo(() => {
    if (showOnlyLoanEligible) {
      return cryptos.filter(crypto => crypto.isLoanAvailable === true);
    }
    return cryptos;
  }, [cryptos, showOnlyLoanEligible]);
  const navigate = useNavigate();

  const renderCryptoChange = (crypto: Crypto) => {
    const quote = extractQuoteWithFallback(crypto as any, currencyKey);
    const { changeAmount, changePercentage } = calculateCryptoChange(quote?.price, quote?.percent_change_24h);

    if (changeAmount === 0 && changePercentage === 0) {
      return (
        <Typography style={{ color: 'white', fontSize: 14, marginTop: 4 }}>
          $0.00 (0.00%)
        </Typography>
      );
    }

    const isPositive = changeAmount > 0;
    const color = isPositive ? "#4edf6d" : "red";

    return (
      <Typography
        style={{
          color: color,
          fontSize: 12,
          marginTop: 4,
          fontWeight: 500
        }}
      >
        {formatter.display(changeAmount, { showSign: true })}
        ({formatPercentage(changePercentage, { showSign: true })})
      </Typography>
    );
  };

  React.useEffect(() => {
    const fetchCryptos = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = UrlConstant.Crypto.GetCryptoList
        const response = await Axios.get(url);
        // Adjust this based on actual API response structure
        // console.log(response, "resss")
        const list = (response.data.data || []).slice();
        list.sort((a: any, b: any) => {
          const aQuote = extractQuoteWithFallback(a, currencyKey);
          const bQuote = extractQuoteWithFallback(b, currencyKey);
          const aCap = aQuote?.market_cap ?? 0;
          const bCap = bQuote?.market_cap ?? 0;
          return bCap - aCap; // Descending by market cap
        });
        setCryptos(list);
      } catch (err) {
        setError('Failed to load crypto market data');
        setCryptos([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCryptos();
  }, [currencyKey]);

  const handleScrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };
  const handleScrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  return (
    <Box
      sx={{
        background: `url(${background}) no-repeat rgba(74, 122, 242, 0.1)`,
        backgroundSize: 'cover',
        borderRadius: '20px',
        padding: '30px',
        marginBottom: '32px',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        margin: '32px auto',
        minHeight: '320px'
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography className="text-white fs-24-desktop" style={{ fontSize: '1.5rem' }}>
            Crypto
          </Typography>
          {/* <Select
            size="small"
            value="All"
            sx={{
              color: 'white',
              background: '#23242A',
              borderRadius: '8px',
              fontSize: '1rem',
              height: '32px',
              minWidth: '60px',
              '.MuiSvgIcon-root': { color: 'white' },
            }}
            disabled
          >
            <MenuItem value="All">All</MenuItem>
          </Select> */}
        </Box>
        <NavLink
          to="/crypto-market"
          style={{
            color: "#4A7AF2",
            textDecoration: "underline !importan",
            textDecorationLine: "underline !important",
          }}
        >
          view all
        </NavLink>
      </Box>
      {/* <ToggleButtonGroup
        value={filter}
        exclusive
        onChange={(_, v) => v && setFilter(v)}
        sx={{
          mb: 2,
          '& .MuiToggleButton-root': {
            color: '#B0B3B8',
            border: 'none',
            borderRadius: '8px',
            fontWeight: 500,
            fontSize: '1rem',
            textTransform: 'none',
            padding: '6px 22px',
            background: 'transparent',
            transition: 'background 0.2s',
            mr: 1,
          },
          '& .Mui-selected': {
            color: '#fff',
            background: '#23242A',
          },
        }}
      >
        <ToggleButton value="Trending">Trending</ToggleButton>
        <ToggleButton value="New">New</ToggleButton>
        <ToggleButton value="Gainers">Gainers</ToggleButton>
        <ToggleButton value="Most Visited">Most Visited</ToggleButton>
      </ToggleButtonGroup> */}
      <Box width='100%' display='flex'>
        {!loading && cryptos.length > 0 && (
          <IconButton
            sx={{ width: '5%', borderRadius: '0px' }}
            onClick={handleScrollLeft}
          >
            <ExpandCircleDownOutlined
              className="text-secondary"
              sx={{ rotate: '-270deg' }}
            />
          </IconButton>
        )}
        <Box
          display="flex"
          gap="10px"
          marginRight="10px"
          ref={scrollRef}
          width={'100%'}
          overflow="auto"
          sx={{
            '&::-webkit-scrollbar': {
              height: '6px',
              display: 'none',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(255,255,255,0.1)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: '',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
          }}
        >
          {loading ? (
            Array.from({ length: 5 }).map((_, idx) => (
              <Box key={idx} sx={{ background: '#23242A', borderRadius: '20px', mt: 2, minWidth: '180px', height: 160, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <Skeleton variant="circular" width={56} height={56} sx={{ mb: 2 }} />
                <Skeleton width={80} height={24} />
                <Skeleton width={60} height={20} />
                <Skeleton width={60} height={16} />
              </Box>
            ))
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : filteredCryptos.length === 0 ? (
            <Typography color="white">
              {showOnlyLoanEligible
                ? 'No loan eligible crypto available'
                : 'No crypto data available'}
            </Typography>

          ) : (
            filteredCryptos.map((crypto: Crypto, idx) => {
              const quote = extractQuoteWithFallback(crypto as any, currencyKey);
              return (
                <Box
                  className=" crpto-card dcard-hover"
                  key={crypto.id || crypto.symbol || idx}
                  sx={{
                    background: '#23242A !important',
                    borderRadius: '20px',
                    mt: 4,
                    p: 2,
                    minWidth: '190px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    position: 'relative',
                    overflow: 'visible',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    navigate('/crypto-detail', {
                      state: { externalID: crypto.cmc_id },
                    });
                  }}
                >
                  {/* Logo */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '-28px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: 'white',
                      borderRadius: '50%',
                      width: 56,
                      height: 56,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                      fontSize: 36,
                      zIndex: 2,
                      m: 0,
                      p: 0,
                    }}
                  >
                    <Avatar
                      src={crypto.logo}
                      alt={crypto.symbol || 'crypto'}
                      sx={{
                        width: 56,
                        height: 56,
                        backgroundColor: "white",
                        objectFit: "contain",
                      }}
                    >
                      {crypto.symbol?.[0] || '₿'}
                    </Avatar>
                  </Box>
                  <Box display="flex" flexDirection="column" alignItems="center" mt={3}>
                    <Box position="relative" display="inline-flex" flexDirection="column" alignItems="center">
                      <Box display="flex" alignItems="center">
                        <Typography style={{ color: 'white', fontWeight: 700, fontSize: 16, textAlign: 'center', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.2, marginLeft: "10px" }} >
                          {crypto.name}
                        </Typography>
                        {crypto.isLoanAvailable && (
                          <Tooltip title="Booster Loan Eligible" arrow placement="top">
                            <span style={{ marginLeft: "10px", marginRight: "-10px" }}>
                              <ThunderIconCirlcle />
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                      <Typography variant="body2" className="text-secondary" fontWeight={400} fontSize={14}>
                        ({crypto.symbol})
                      </Typography>
                      {/* {crypto.isLoanAvailable && ( */}
                      {/* <Tooltip title="Booster Loan Eligible" arrow placement="top">
                        <span style={{ position: 'absolute', right: -15, top: '22%', transform: 'translateY(-50%)' }}>
                          <ThunderIconCirlcle />
                        </span>
                      </Tooltip> */}
                      {/* )} */}
                    </Box>
                    {/* Price */}
                    <Typography
                      style={{
                        color: 'white',
                        fontSize: 14,
                        fontWeight: 700,
                        marginTop: 6,
                      }}
                    >
                      {formatter.price(quote?.price).display ?? '-'}
                    </Typography>

                    {renderCryptoChange(crypto)}
                  </Box>
                </Box>
              )
            })
          )}
        </Box>

        {!loading && cryptos.length > 0 && (
          <IconButton
            sx={{ width: '5%', borderRadius: '0px' }}
            onClick={handleScrollRight}
          >
            <ExpandCircleDownOutlined
              sx={{ rotate: '-90deg' }}
            />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}

function TopStocksSection() {
  const [stocks, setStocks] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const formatter = usePriceFormatter()

  React.useEffect(() => {
    const fetchStocks = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await Axios.get(UrlConstant.TopStocks.GetTopStocksByMarketCap);
        const list = response.data?.data?.stocks || [];
        setStocks(list);
      } catch (err) {
        setError('Failed to load top stocks data');
        setStocks([]);
      } finally {
        setLoading(false);
      }
    };
    fetchStocks();
  }, []);

  const handleScrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const handleScrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  return (
    <Box
      sx={{
        background: `url(${background}) no-repeat rgba(74, 122, 242, 0.1)`,
        backgroundSize: 'cover',
        borderRadius: '20px',
        padding: '30px',
        marginBottom: '32px',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        margin: '32px auto',
        minHeight: '320px'
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography className="text-white fs-24-desktop" style={{ fontSize: '1.5rem' }}>
            Top Stocks
          </Typography>
        </Box>
        <NavLink
          to="/stock-market"
          style={{
            color: '#4A7AF2',
            textDecoration: 'underline !important',
            textDecorationLine: 'underline !important',
          }}
        >
          view all
        </NavLink>
      </Box>

      <Box width="100%" display="flex">
        {!loading && stocks.length > 0 && (
          <IconButton sx={{ width: '5%', borderRadius: '0px' }} onClick={handleScrollLeft}>
            <ExpandCircleDownOutlined
              className="text-secondary"
              sx={{ rotate: '-270deg' }}
            />
          </IconButton>
        )}

        <Box
          display="flex"
          gap="10px"
          marginRight="10px"
          ref={scrollRef}
          width="100%"
          overflow="auto"
          sx={{
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          }}
        >
          {loading ? (
            Array.from({ length: 5 }).map((_, idx) => (
              <Box
                key={idx}
                sx={{
                  background: '#23242A',
                  borderRadius: '20px',
                  mt: 2,
                  minWidth: '180px',
                  height: 160,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Skeleton variant="circular" width={56} height={56} sx={{ mb: 2 }} />
                <Skeleton width={80} height={24} />
                <Skeleton width={60} height={20} />
                <Skeleton width={60} height={16} />
              </Box>
            ))
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : stocks.length === 0 ? (
            <Typography color="white">No stock data available</Typography>
          ) : (
            stocks.map((stock: any, idx: number) => {
              const change = stock.Change ?? 0;
              const changePct = stock.ChangePercentage ?? 0;
              const isPositive = change > 0;
              const changeColor = change === 0 ? 'white' : isPositive ? '#4edf6d' : 'red';

              return (
                <Box
                  className="crpto-card dcard-hover"
                  key={stock._id || stock.symbol || idx}
                  sx={{
                    background: '#23242A !important',
                    borderRadius: '20px',
                    mt: 4,
                    p: 2,
                    minWidth: '190px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    position: 'relative',
                    overflow: 'visible',
                    cursor: 'pointer',
                  }}
                  onClick={() =>
                    navigate('/stockdetail', {
                      state: {
                        symbol: stock.symbol,
                        icon: stock.branding?.icon_url ?? stock.branding?.logo_url,
                        title: 'Top Stocks',
                      },
                    })
                  }
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '-28px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: 'white',
                      borderRadius: '50%',
                      width: 56,
                      height: 56,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                      zIndex: 2,
                    }}
                  >
                    <Avatar
                      src={stock.branding?.icon_url ?? stock.branding?.logo_url}
                      alt={stock.symbol}
                      sx={{ width: 56, height: 56, backgroundColor: 'white', objectFit: 'contain' }}
                    >
                      {stock.symbol?.[0]}
                    </Avatar>
                  </Box>

                  <Box display="flex" flexDirection="column" alignItems="center" mt={3}>
                    <Typography
                      style={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: 16,
                        textAlign: 'center',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        lineHeight: 1.2,
                      }}
                    >
                      {stock.displayName}
                    </Typography>
                    <Typography variant="body2" className="text-secondary" fontWeight={400} fontSize={14}>
                      ({stock.symbol})
                    </Typography>

                    <Typography style={{ color: 'white', fontSize: 14, fontWeight: 700, marginTop: 6 }}>
                      {formatter.display(stock.Last ?? stock.stockPrice)}
                    </Typography>

                    <Typography style={{ color: changeColor, fontSize: 12, marginTop: 4, fontWeight: 500 }}>
                      {formatter.display(change, { showSign: true })}
                      ({formatPercentage(
                        changePct < 0
                          ? -Math.abs(changePct)
                          : Math.abs(changePct),
                        { showSign: true }
                      )})
                    </Typography>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>

        {!loading && stocks.length > 0 && (
          <IconButton sx={{ width: '5%', borderRadius: '0px' }} onClick={handleScrollRight}>
            <ExpandCircleDownOutlined sx={{ rotate: '-90deg' }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}

function MarketSection() {
  const [activeTab, setActiveTab] = React.useState<'crypto' | 'stocks'>('crypto');

  const [cryptos, setCryptos] = React.useState<Crypto[]>([]);
  const [cryptoLoading, setCryptoLoading] = React.useState(true);
  const [cryptoError, setCryptoError] = React.useState<string | null>(null);

  const [stocks, setStocks] = React.useState<any[]>([]);
  const [stocksLoading, setStocksLoading] = React.useState(true);
  const [stocksError, setStocksError] = React.useState<string | null>(null);

  const scrollRef = React.useRef<HTMLDivElement>(null);
  const showOnlyLoanEligible = useAppSelector((state) => state.cryptoFilter.showOnlyLoanEligible);
  const navigate = useNavigate();
  const formatter = usePriceFormatter()
  const currencyInfo = useAppSelector((state) => state.currency);
  const currencyKey = currencyInfo.effectiveCurrency || 'USD';
  const filteredCryptos = React.useMemo(() => {
    if (showOnlyLoanEligible) return cryptos.filter(c => c.isLoanAvailable === true);
    return cryptos;
  }, [cryptos, showOnlyLoanEligible]);

  const renderCryptoChange = (crypto: Crypto) => {
    const quote = extractQuoteWithFallback(crypto as any, currencyKey);
    const { changeAmount, changePercentage } = calculateCryptoChange(quote?.price, quote?.percent_change_24h);

    if (changeAmount === 0 && changePercentage === 0) {
      return (
        <Typography style={{ color: 'white', fontSize: 14, marginTop: 4 }}>
          $0.00 (0.00%)
        </Typography>
      );
    }

    const isPositive = changeAmount > 0;
    const color = isPositive ? "#4edf6d" : "red";

    return (
      <Typography
        style={{
          color: color,
          fontSize: 12,
          marginTop: 4,
          fontWeight: 500
        }}
      >
        {formatter.display(changeAmount, { showSign: true })}
        ({formatPercentage(changePercentage, { showSign: true })})
      </Typography>
    );
  };

  React.useEffect(() => {
    const fetchCryptos = async () => {
      setCryptoLoading(true);
      setCryptoError(null);
      try {
        const url = UrlConstant.Crypto.GetCryptoList
        const response = await Axios.get(url);
        const list = (response.data.data || []).slice();
        list.sort((a: any, b: any) => {
          const aQuote = extractQuoteWithFallback(a, currencyKey);
          const bQuote = extractQuoteWithFallback(b, currencyKey);
          const aCap = aQuote?.market_cap ?? 0;
          const bCap = bQuote?.market_cap ?? 0;
          return bCap - aCap; // Descending by market cap
        });
        setCryptos(list);
      } catch (err) {
        setCryptoError('Failed to load crypto market data');
        setCryptos([]);
      } finally {
        setCryptoLoading(false);
      }
    };
    fetchCryptos();
  }, [currencyKey]);

  React.useEffect(() => {
    const fetchStocks = async () => {
      setStocksLoading(true);
      setStocksError(null);
      try {
        const response = await Axios.get(UrlConstant.TopStocks.GetTopStocksByMarketCap);
        setStocks(response.data?.data?.stocks || []);
      } catch {
        setStocksError('Failed to load top stocks data');
        setStocks([]);
      } finally {
        setStocksLoading(false);
      }
    };
    fetchStocks();
  }, [currencyKey]);

  const handleScrollLeft = () => scrollRef.current?.scrollBy({ left: -200, behavior: 'smooth' });
  const handleScrollRight = () => scrollRef.current?.scrollBy({ left: 200, behavior: 'smooth' });

  const isLoading = activeTab === 'crypto' ? cryptoLoading : stocksLoading;
  const error = activeTab === 'crypto' ? cryptoError : stocksError;
  const items = activeTab === 'crypto' ? filteredCryptos : stocks;

  return (
    <Box
      sx={{
        background: `url(${background}) no-repeat rgba(74, 122, 242, 0.1)`,
        backgroundSize: 'cover',
        borderRadius: '20px',
        padding: '30px',
        marginBottom: '32px',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        margin: '32px auto',
        minHeight: '320px',
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <ToggleButtonGroup
          value={activeTab}
          exclusive
          onChange={(_, val) => { if (val) { setActiveTab(val); scrollRef.current?.scrollTo({ left: 0 }); } }}
          sx={{
            background: '#23272F',
            borderRadius: '12px',
            p: 0.5,
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            '& .MuiToggleButton-root': {
              color: 'white',
              fontWeight: 600,
              fontSize: '1rem',
              border: 0,
              borderRadius: '10px !important',
              px: 3,
              py: 1,
              transition: 'background 0.2s',
            },
            '& .Mui-selected': {
              background: '#4A7AF2',
              color: 'white',
            },
          }}
        >
          <ToggleButton value="crypto">Top Crypto</ToggleButton>
          <ToggleButton value="stocks">Top Stocks</ToggleButton>
        </ToggleButtonGroup>

        {/* View all link */}
        <NavLink
          to={activeTab === 'crypto' ? '/crypto-market' : '/stock-market'}
          style={{ color: '#4A7AF2', textDecoration: 'underline' }}
        >
          view all
        </NavLink>
      </Box>

      {/* Cards Row */}
      <Box width="100%" display="flex">
        {!isLoading && items.length > 0 && (
          <IconButton sx={{ width: '5%', borderRadius: '0px' }} onClick={handleScrollLeft}>
            <ExpandCircleDownOutlined className="text-secondary" sx={{ rotate: '-270deg' }} />
          </IconButton>
        )}

        <Box
          ref={scrollRef}
          display="flex"
          gap="10px"
          marginRight="10px"
          width="100%"
          overflow="auto"
          sx={{ '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}
        >
          {/* Loading skeletons */}
          {isLoading &&
            Array.from({ length: 5 }).map((_, idx) => (
              <Box key={idx} sx={{ background: '#23242A', borderRadius: '20px', mt: 2, minWidth: '180px', height: 160, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Skeleton variant="circular" width={56} height={56} sx={{ mb: 2 }} />
                <Skeleton width={80} height={24} />
                <Skeleton width={60} height={20} />
                <Skeleton width={60} height={16} />
              </Box>
            ))}

          {/* Error */}
          {!isLoading && error && <Typography color="error">{error}</Typography>}

          {/* Empty */}
          {!isLoading && !error && items.length === 0 && (
            <Typography color="white">
              {activeTab === 'crypto' && showOnlyLoanEligible ? 'No loan eligible crypto available' : `No ${activeTab === 'crypto' ? 'crypto' : 'stock'} data available`}
            </Typography>
          )}

          {/* Crypto cards */}
          {!isLoading && !error && activeTab === 'crypto' &&
            filteredCryptos.map((crypto: Crypto, idx) => {
              const quote = extractQuoteWithFallback(crypto as any, currencyKey);
              return (
                <Box
                  className=" crpto-card dcard-hover"
                  key={crypto.id || crypto.symbol || idx}
                  sx={{
                    background: '#23242A !important',
                    borderRadius: '20px',
                    mt: 4,
                    p: 2,
                    minWidth: '190px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    position: 'relative',
                    overflow: 'visible',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    navigate('/crypto-detail', {
                      state: { externalID: crypto.cmc_id },
                    });
                  }}
                >
                  {/* Logo */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '-28px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: 'white',
                      borderRadius: '50%',
                      width: 56,
                      height: 56,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                      fontSize: 36,
                      zIndex: 2,
                      m: 0,
                      p: 0,
                    }}
                  >
                    <Avatar
                      src={crypto.logo}
                      alt={crypto.symbol || 'crypto'}
                      sx={{
                        width: 56,
                        height: 56,
                        backgroundColor: "white",
                        objectFit: "contain",
                      }}
                    >
                      {crypto.symbol?.[0] || '₿'}
                    </Avatar>
                  </Box>
                  <Box display="flex" flexDirection="column" alignItems="center" mt={3}>
                    <Box position="relative" display="inline-flex" flexDirection="column" alignItems="center">
                      <Box display="flex" alignItems="center">
                        <Typography style={{ color: 'white', fontWeight: 700, fontSize: 16, textAlign: 'center', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.2, marginLeft: "10px" }} >
                          {crypto.name}
                        </Typography>
                        {crypto.isLoanAvailable && (
                          <Tooltip title="Booster Loan Eligible" arrow placement="top">
                            <span style={{ marginLeft: "10px", marginRight: "-10px" }}>
                              <ThunderIconCirlcle />
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                      <Typography variant="body2" className="text-secondary" fontWeight={400} fontSize={14}>
                        ({crypto.symbol})
                      </Typography>
                      {/* {crypto.isLoanAvailable && ( */}
                      {/* <Tooltip title="Booster Loan Eligible" arrow placement="top">
                        <span style={{ position: 'absolute', right: -15, top: '22%', transform: 'translateY(-50%)' }}>
                          <ThunderIconCirlcle />
                        </span>
                      </Tooltip> */}
                      {/* )} */}
                    </Box>
                    {/* Price */}
                    <Typography
                      style={{
                        color: 'white',
                        fontSize: 14,
                        fontWeight: 700,
                        marginTop: 6,
                      }}
                    >
                      {formatter.price(quote?.price).display ?? '-'}
                    </Typography>

                    {renderCryptoChange(crypto)}
                  </Box>
                </Box>
              )
            })}

          {/* Stock cards */}
          {!isLoading && !error && activeTab === 'stocks' &&
            stocks.map((stock: any, idx: number) => {
              const change = stock.Change ?? 0;
              const changePct = stock.ChangePercentage ?? 0;
              const changeColor = change === 0 ? 'white' : change > 0 ? '#4edf6d' : 'red';
              return (
                <Box
                  className="crpto-card dcard-hover"
                  key={stock._id || stock.symbol || idx}
                  sx={{ background: '#23242A !important', borderRadius: '20px', mt: 4, p: 2, minWidth: '190px', display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', position: 'relative', overflow: 'visible', cursor: 'pointer' }}
                  onClick={() => navigate('/stockdetail', { state: { symbol: stock.symbol, icon: stock.branding?.icon_url ?? stock.branding?.logo_url, title: 'Top Stocks' } })}
                >
                  <Box sx={{ position: 'absolute', top: '-28px', left: '50%', transform: 'translateX(-50%)', background: 'white', borderRadius: '50%', width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.10)', zIndex: 2 }}>
                    <Avatar src={stock.branding?.icon_url ?? stock.branding?.logo_url} alt={stock.symbol} sx={{ width: 56, height: 56, backgroundColor: 'white', objectFit: 'contain' }}>
                      {stock.symbol?.[0]}
                    </Avatar>
                  </Box>
                  <Box display="flex" flexDirection="column" alignItems="center" mt={3}>
                    <Typography style={{ color: 'white', fontWeight: 700, fontSize: 16, textAlign: 'center', wordBreak: 'break-word', lineHeight: 1.2 }}>
                      {stock.displayName}
                    </Typography>
                    <Typography variant="body2" className="text-secondary" fontWeight={400} fontSize={14}>
                      ({stock.symbol})
                    </Typography>
                    <Typography style={{ color: 'white', fontSize: 14, fontWeight: 700, marginTop: 6 }}>
                      {formatter.display(stock.Last ?? stock.stockPrice)}
                    </Typography>
                    <Typography style={{ color: changeColor, fontSize: 12, marginTop: 4, fontWeight: 500 }}>
                      {formatter.display(change, { showSign: true })}
                      ({formatPercentage(
                        changePct < 0
                          ? -Math.abs(changePct)
                          : Math.abs(changePct),
                        { showSign: true }
                      )})
                    </Typography>
                  </Box>
                </Box>
              );
            })}
        </Box>

        {!isLoading && items.length > 0 && (
          <IconButton sx={{ width: '5%', borderRadius: '0px' }} onClick={handleScrollRight}>
            <ExpandCircleDownOutlined sx={{ rotate: '-90deg' }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}

function Dashboard() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const customerInfo = getCustomerInfoFromLocalStorage();
  const isPremium = customerInfo?.entitlements?.active?.Premium?.isActive;
  const formatter = usePriceFormatter()
  const currencyInfo = useAppSelector((state) => state.currency);

  const currencyKey = currencyInfo.effectiveCurrency || 'USD';

  // AI Picks Starts
  const [allAiPickData, setAllAiPickData] = useState<any>({});
  const [aiPicksList, setAiPicksList] = useState([]);
  const [aiPicksLoading, setAiPicksLoading] = useState<any>(false);
  const [buyOrSell, setBuyOrSell] = useState<any>(Constant.BUY);
  const [stockOrETF, setStockOrETF] = useState<any>(Constant.STOCK);
  const [selectedCategory, setSelectedCategory] = useState<String>(Constant.POPULAR);
  // AI Picks Starts

  const [watchList, setWatchList] = useState<any>([]);
  const [watchlistLoading, setWatchlistLoading] = useState<any>(false);
  const [allMyPickData, setAllMyPickData] = useState<any>([]);
  const [myPickData, setMyPickData] = useState<any>([]);
  const [myPicsLoading, setMyPicsLoading] = useState<any>(false);

  // Market Movers

  const [marketMoversLoading, setMarketMoversLoading] = useState<any>(false);
  const [marketMoversType, setMarketMoversType] = useState<any>("gainers");
  const [marketMovers, setMarketMovers] = useState<any>([]);
  const [marketMoversData, setMarketMoversData] = useState<any>([]);
  const [marketsignalselect, setmarketsignalselect] = useState<any>("bullish");
  const [marketSignalCategories, setMarketSignalCategories] = useState<any>("bullish");
  const [myPicsType, setMyPicsType] = useState<any>("STOCK");
  const [myPicsPreference, setMyPicsPreference] = useState<any>("BUY");
  const [marketMoversFilter, setMarketMoversFilter] = useState<string>("L");
  const [marketSignalOpt, setMarketsignalOpt] = useState<any>("");
  const [myPicsOpt, setMyPicsOpt] = useState<String>("Live");
  const [boosterLoanRepaymentData, setBoosterLoanRepaymentData] = useState<any>([]);
  const [boosterLoanLoading, setBoosterLoanLoading] = useState<any>(false);

  const scrollRef2 = useRef<HTMLDivElement>(null);
  const scrollRef3 = useRef<HTMLDivElement>(null);
  const scrollRef4 = useRef<HTMLDivElement>(null);
  const scrollRef5 = useRef<HTMLDivElement>(null);
  const scrollRef6 = useRef<HTMLDivElement>(null);
  const scrollRef7 = useRef<HTMLDivElement>(null);
  // Market Signals

  const [leftarrow2, setleftarrow2] = useState<any>(false);
  const [leftarrow3, setleftarrow3] = useState<any>(false);
  const [leftarrow4, setleftarrow4] = useState<any>(false);
  const [leftarrow5, setleftarrow5] = useState<any>(false);
  const [leftarrow6, setleftarrow6] = useState<any>(false);
  const [leftarrow7, setleftarrow7] = useState<any>(false);
  const [marketSignalFilers, setMarketSignalFilers] = useState<any>([]);
  const [marketSignalFilersData, setMarketSignalFilersData] = useState<any>([]);
  const [marketSignalsLoading, setMarketSignalsLoading] = useState<any>(false);

  // Trade Ideas
  const [tradeIdeas, setTradeIdeas] = useState<any>({});
  const [tradeIdeaLoading, setTradeIdeaLoading] = useState<boolean>(false);
  const [chartLoading, setChartLoading] = useState<boolean>(false);
  const [tradeIdeaBullishChartData, setTradeIdeaBullishChartData] = useState<any>([]);
  const [tradeIdeasBearishChartData, setTradeIdeasBearishChartData] = useState<any>([]);
  const [tradeType, setTradeType] = useState<'stock' | 'crypto'>('crypto');
  const [tradeIdeasError, setTradeIdeasError] = useState<string>('');

  // Market Trackers
  const [marketTrackers, setMarketTrackers] = useState<any>({});
  const [marketTrackersLoading, setMarketTrackersLoading] = useState<any>(false);

  const [watchlistFilter, setWatchlistFilter] = useState('all');
  const showOnlyLoanEligible = useAppSelector((state) => state.cryptoFilter.showOnlyLoanEligible);
  const user = getUserFromLocalStorage();

  // console.log("aiPicksList", aiPicksList)
  const getSelectedAiPicksData = async (
    allStockData,
    categoryKey: string,
    actionKey: string
  ) => {
    if (allStockData?.[categoryKey]?.[actionKey]) {
      const list = allStockData[categoryKey][actionKey].map((stock: any) => {
        const item = stock.stockdata;
        const v1 = item.regularMarketPrice ?? 0;
        const v2 = item.Last ?? 0;
        const diffrence = v2 - v1;
        const div = diffrence / v1;
        const res = div * 100;
        const itemDetail = {
          displayName: item.displayName,
          stockPrice: item.Last,
          res: res,
          diffrence: diffrence,
          branding: item.branding,
          symbol: item.symbol,
          icon: item.branding?.icon_url ?? item.branding?.logo_url,
          Change: item.Change,
          ChangePercentage: item.ChangePercentage,
        };

        return itemDetail;
      });
      setAiPicksList(list);
    }
  };
  const handleScrollLeft = () => {
    if (scrollRef2.current) {
      scrollRef2.current.scrollBy({
        left: 300, // Amount to scroll
        behavior: "smooth", // Enables smooth scrolling
      });
      setleftarrow2(true)
    }
  };
  const handleScrollLeft3 = () => {
    if (scrollRef3.current) {
      scrollRef3.current.scrollBy({
        left: 300, // Amount to scroll
        behavior: "smooth", // Enables smooth scrolling
      });
    }
    setleftarrow3(true)

  };
  const handleScrollLeft4 = () => {
    if (scrollRef4.current) {
      scrollRef4.current.scrollBy({
        left: 300, // Amount to scroll
        behavior: "smooth", // Enables smooth scrolling
      });
    }
    setleftarrow4(true)

  };
  const handleScrollLeft5 = () => {
    if (scrollRef5.current) {
      scrollRef5.current.scrollBy({
        left: 300, // Amount to scroll
        behavior: "smooth", // Enables smooth scrolling
      });
    }
    setleftarrow5(true)

  };
  const handleScrollLeft6 = () => {
    if (scrollRef6.current) {
      scrollRef6.current.scrollBy({
        left: 300, // Amount to scroll
        behavior: "smooth", // Enables smooth scrolling
      });
    }
    setleftarrow6(true)

  };
  const handleScrollLeft7 = () => {
    if (scrollRef7.current) {
      scrollRef7.current.scrollBy({
        left: 300, // Amount to scroll
        behavior: "smooth", // Enables smooth scrolling
      });
    }
    setleftarrow7(true)

  };
  const handleScrollRight = () => {
    if (scrollRef2.current) {
      scrollRef2.current.scrollBy({
        left: -300, // Amount to scroll
        behavior: "smooth", // Enables smooth scrolling
      });
    }
  };
  const handleScrollRight2 = () => {
    if (scrollRef3.current) {
      scrollRef3.current.scrollBy({
        left: -300, // Amount to scroll
        behavior: "smooth", // Enables smooth scrolling
      });
    }
  };
  const handleScrollRight3 = () => {
    if (scrollRef4.current) {
      scrollRef4.current.scrollBy({
        left: -300, // Amount to scroll
        behavior: "smooth", // Enables smooth scrolling
      });
    }
  };
  const handleScrollRight4 = () => {
    if (scrollRef5.current) {
      scrollRef5.current.scrollBy({
        left: -300, // Amount to scroll
        behavior: "smooth", // Enables smooth scrolling
      });
    }
  };
  const handleScrollRight5 = () => {
    if (scrollRef6.current) {
      scrollRef6.current.scrollBy({
        left: -300, // Amount to scroll
        behavior: "smooth", // Enables smooth scrolling
      });
    }
  };
  const handleScrollRight7 = () => {
    if (scrollRef7.current) {
      scrollRef7.current.scrollBy({
        left: -300, // Amount to scroll
        behavior: "smooth", // Enables smooth scrolling
      });
    }
  };
  const handleAiPickBuyOrSell = (event: SelectChangeEvent) => {
    const value = event.target.value as string;
    setBuyOrSell(value);
    getSelectedAiPicksData(allAiPickData, selectedCategory.toString(), value);
  };
  const handleStockOrETF = (event: SelectChangeEvent) => {
    setStockOrETF(event.target.value as string);
  };
  const handleSelectedCategory = (
    event: React.MouseEvent<HTMLElement>,
    newCategory: string
  ) => {
    setSelectedCategory(newCategory);
    if (newCategory === Constant.FOLLOWING) {
      getFollowiList();
    } else {
      getAiPickList(newCategory);
    }
  };

  const handleChangeMarketMovers = (event: SelectChangeEvent) => {
    setMarketMoversType(event.target.value as string);
  };

  const handleChangeMarketSingnalsSelect = (event: SelectChangeEvent) => {
    setmarketsignalselect(event.target.value as string);
    const data = marketSignalFilers.find((item) => item.signal === event.target.value);
    setMarketSignalCategories(data)
    setMarketsignalOpt(data.categories[0].name);
  };
  const handleChangemypicks1 = (event: SelectChangeEvent) => {
    setMyPicsType(event.target.value as string);
  };
  const handleChangeMyPicksPreference = (event: SelectChangeEvent) => {
    setMyPicsPreference(event.target.value as string);
    const data =
      allMyPickData.length &&
      allMyPickData.filter(
        (item: any) => item.recommedationType === event.target.value as string
      );
    setMyPickData(data ? data : []);
  };

  const handleChangeMarketMoversFilter = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string
  ) => {
    setMarketMoversFilter(newAlignment);

    const selected = marketMovers?.find(
      (item) => item.key === newAlignment
    );

    setMarketMoversData(selected?.data ?? []);
  };

  const onMarketSignalFilterChange = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string
  ) => {
    setMarketsignalOpt(newAlignment);
  };
  const handleChangeMyPicksFilter = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string
  ) => {
    setMyPicsOpt(newAlignment);
  };

  const getSelectedData = async (
    allStockData,
    categoryKey: string,
    actionKey: string
  ) => {
    if (allStockData?.[categoryKey]?.[actionKey]) {
      const list = allStockData[categoryKey][actionKey].map((stock: any) => {
        const item = stock.stockdata;
        const v1 = item.regularMarketPrice ?? 0;
        const v2 = item.Last ?? 0;
        const diffrence = v2 - v1;
        const div = diffrence / v1;
        const res = div * 100;
        const itemDetail = {
          displayName: item.displayName,
          stockPrice: item.Last,
          res: res,
          diffrence: diffrence,
          branding: item.branding,
          symbol: item.symbol,
          icon: item.branding?.icon_url ?? item.branding?.logo_url,
          Change: item.Change,
          ChangePercentage: item.ChangePercentage,
        };

        return itemDetail;
      });
      setAiPicksList(list);
    }
  };

  const getAiPickList = async (category) => {
    dispatch(startLoading());
    setAiPicksLoading(true);
    try {
      const response = await Axios.get(
        `${UrlConstant.AiPicks.GetAiPicks}?type=${stockOrETF}`
      );
      const resData = response.data;
      if (resData.result === true) {
        setAllAiPickData(resData.data);
        getSelectedData(resData.data, category, buyOrSell.toString());
      }
    } catch (error: any) {
      console.error("ERRR : ", error.response.status);
      if (error.response.status === 403) {
        dispatch(logout());
        navigate("/login");
      }
    } finally {
      dispatch(endLoading());
      setAiPicksLoading(false);
    }
  };

  const getMyPickList = async () => {
    dispatch(startLoading());
    setMyPicsLoading(true);
    let URL;

    if (myPicsOpt == "Live") {
      URL = UrlConstant.MyPicks.GetMyPicksLive.replace("{0}", myPicsType);
    } else {
      URL = UrlConstant.MyPicks.GetMyPicksHistory.replace("{0}", myPicsType);
    }

    try {
      const response = await Axios.get(URL);
      const resData = response.data;
      if (resData.result === true) {
        setAllMyPickData(resData.data);
        const data =
          resData.data.length &&
          resData.data.filter(
            (item: any) => item.recommedationType === myPicsPreference
          );
        setMyPickData(data ? data : []);
      }
      setMyPicsLoading(false);
    } catch (error: any) {
      console.error("ERRR : ", error.response.status);
      if (error.response.status === 403) {
        dispatch(logout());
        navigate("/login");
      }
      setMyPicsLoading(false);
    } finally {
      dispatch(endLoading());

    }
  };

  const getWatchList = async () => {
    dispatch(startLoading());
    setWatchlistLoading(true);
    try {
      const response = await Axios.get(UrlConstant.WachList.GetWatchList);
      const resData = response.data;
      if (resData.result === true) {
        const list = resData.data.map((item) => {
          const isCrypto = item.type === 'crypto';
          if (isCrypto) {

            const quote = extractQuoteWithFallback(item as any, currencyKey);
            const { changeAmount, changePercentage } = calculateCryptoChange(
              quote?.price,
              quote?.percent_change_24h
            );
            return {
              type: 'crypto',
              icon: item.logo,
              name: item.name,
              symbol: item.symbol,
              price: quote?.price,
              changeAmount,
              changePercentage,
              externalID: item.cmc_id,
              isLoanAvailable: item.isLoanAvailable,
            };
          } else {
            return {
              type: 'stock',
              icon: item.branding?.icon_url ?? item.branding?.logo_url,
              name: item.displayName,
              symbol: item.symbol,
              price: item.Last ?? item.stockPrice ?? item.regularMarketPrice,
              changeAmount: item.changeAmount,
              changePercentage: item.changePercentage,
              branding: item.branding,
              isLoanAvailable: item.isLoanAvailable,
            };
          }
        });
        setWatchList(list);
      }
    } catch (error: any) {
      console.error("ERRR : ", error);
    } finally {
      dispatch(endLoading());
      setWatchlistLoading(false);
    }
  };

  const getBoosterLoanRepayment = async () => {
    setBoosterLoanLoading(true);
    try {
      const response = await Axios.get(
        UrlConstant.BoosterLoan.GetBoosterLoanRepayment
      );
      const resData = response.data;
      if (resData.status === true) {
        setBoosterLoanRepaymentData(resData.data);
      }
    } catch (error: any) {
      console.error("ERRR : ", error);
    } finally {
      setBoosterLoanLoading(false);
    }
  };

  const getFollowiList = async () => {
    dispatch(startLoading());

    try {
      const response = await Axios.post(
        UrlConstant.AiPicks.GetAiPicksOfFollowing.replace("{0}", stockOrETF)
      );
      const resData = response.data;
      if (resData.result === true) {
        setAllAiPickData(resData.data);
        const list = resData.data[buyOrSell].map((stock: any) => {
          const item = stock.stockdata;
          const v1 = item.regularMarketPrice ?? 0;
          const v2 = item.Last ?? 0;
          const diffrence = v2 - v1;
          const div = diffrence / v1;
          const res = div * 100;
          const itemDetail = {
            displayName: item.displayName,
            stockPrice: item.Last,
            res: res,
            diffrence: diffrence,
            branding: item.branding,
            symbol: item.symbol,
            icon: item.branding?.icon_url ?? item.branding?.logo_url,
          };

          return itemDetail;
        });
        setAiPicksList(list);
      }
    } catch (error: any) {
      console.error("ERRR : ", error);
    } finally {
      dispatch(endLoading());
    }
  };
  // const checkAccountStatus = async () => {
  //   try {
  //     const response = await Axios.get(
  //       UrlConstant.TradingAccount.CheckAccountStatus
  //     );
  //     const resData = response.data;
  //     console.log("AC Status : ", resData);
  //     if (resData.status === true) {
  //       setApplicationStatus(resData.data.status);
  //       setDocumentRequired(resData.data.isDocumentUploadRequired);
  //       setApplicationId(resData.data.applicationId);
  //       if (
  //         resData.data.status !== "" &&
  //         resData.data.isDocumentUploadRequired === false &&
  //         applicationId &&
  //         applicationId !== ""
  //       ) {
  //         setDocumentSubmitted(true);
  //       }
  //     }
  //   } catch (error: any) {
  //     console.error("ERRR : ", error);
  //   }
  // };

  const hasDataFor = (key: string) =>
    marketMovers?.some(
      (item) => item.key === key && item.data?.length > 0
    );

  const getMarketMovers = async () => {
    setMarketMoversLoading(true);
    try {
      const response = await Axios.get(
        UrlConstant.MarketMovers.GetMarketMovers.replace(
          "{0}",
          marketMoversType
        )
      );

      const resData = response.data;
      const order = ["L", "M", "S"];

      const sorted = [...resData.data].sort(
        (a, b) => order.indexOf(a.key) - order.indexOf(b.key)
      );

      setMarketMovers(sorted);

      const firstAvailable = sorted.find(
        (item) => item.data?.length > 0
      );

      if (firstAvailable) {
        setMarketMoversFilter(firstAvailable.key);
        setMarketMoversData(firstAvailable.data);
      } else {
        setMarketMoversFilter("L");
        setMarketMoversData([]);
      }
    } catch (error) {
      console.error("ERRR : ", error);
    } finally {
      setMarketMoversLoading(false);
    }
  };

  const getMarketSignalsFilters = async () => {
    setMarketSignalsLoading(true);
    try {
      const response = await Axios.get(
        UrlConstant.Market.GetMarketSignalsFilters
      );
      const resData = response.data;
      if (resData.status === true) {
        setMarketSignalFilers(resData.data);
        setMarketsignalOpt(resData.data[marketsignalselect]?.[0].key);
      }
    } catch (error: any) {
      console.error("ERRR : ", error);
    } finally {
    }
  };

  const getMarketSignalsCategories = async () => {
    setMarketSignalsLoading(true);
    try {
      const response = await Axios.get(
        UrlConstant.Market.GetMArketSignalCategories
      );
      const resData = response.data;
      if (resData.status === true) {
        setMarketSignalFilers(resData.data);
        setmarketsignalselect(resData.data[0].signal);
        setMarketSignalCategories(resData.data[0]);
        setMarketsignalOpt(resData.data[0].categories[0].name);
      }
    } catch (error: any) {
      console.error("ERRR : ", error);
    } finally {
    }
  }

  const getMarketSignalsFilterData = async () => {
    setMarketSignalsLoading(true);

    try {
      const response = await Axios.get(
        UrlConstant.Market.GetMarketSignalByCategory.replace("{0}", marketsignalselect).replace("{1}", marketSignalOpt))
      const resData = response.data;
      if (resData.status === true) {
        setMarketSignalFilersData(resData.data ?? []);
      }
    } catch (error: any) {
      console.error("ERRR : ", error);
    } finally {
      setMarketSignalsLoading(false);
    }
  };

  // get Trade Ideas
  const getTradeIdeas = async (type: 'stock' | 'crypto' = tradeType) => {
    dispatch(startLoading());
    setTradeIdeaLoading(true);
    setTradeIdeasError('');
    try {
      const endpoint =
        type === 'stock'
          ? UrlConstant.TradeIdea.getTradeIdea
          : showOnlyLoanEligible
            ? `${UrlConstant.Crypto.GetTradeIdeas}?isLoanAvailable=true`
            : UrlConstant.Crypto.GetTradeIdeas;

      const response = await Axios.get(endpoint);
      const resData = response.data;

      if (resData.status === true) {
        setTradeIdeas(resData.data);
        setTradeIdeasError('');
      } else {
        // API returned false status
        setTradeIdeas({});
        setTradeIdeasError(`No ${type === 'stock' ? 'stock' : 'crypto'} trade ideas available at the moment.`);
      }
    } catch (error: any) {
      console.error("Error fetching trade ideas:", error);
      setTradeIdeas({}); // Clear old data
      setTradeIdeasError(
        `Failed to load ${type === 'stock' ? 'stock' : 'crypto'} trade ideas. Please try again later.`
      );
    } finally {
      setTradeIdeaLoading(false);
      dispatch(endLoading());
    }
  };

  // getMarketTraders
  const getMarketTrackers = async () => {
    dispatch(startLoading());
    setMarketTrackersLoading(true);
    try {
      const response = await Axios.get(UrlConstant.MarketTrackers.getMarketTrackers);
      const resData = response.data;
      if (resData.status === true) {
        setMarketTrackers(resData.data);
      }
      setMarketTrackersLoading(false);
    } catch (error: any) {
      console.error("ERRR : ", error);
      setMarketTrackersLoading(false);
    } finally {
      dispatch(endLoading());
    }
  }

  React.useEffect(() => {
    if (selectedCategory === Constant.FOLLOWING) {
      getFollowiList();
    } else {
      getAiPickList(selectedCategory);
    }
  }, [selectedCategory, stockOrETF]);

  useEffect(() => {
    getWatchList();
    getMarketTrackers()
    getMarketSignalsCategories();
    getBoosterLoanRepayment();
  }, []);

  // Re-fetch watchlist when other parts of the app trigger a refresh
  const watchlistVersion = useAppSelector((state) => state.watchlist?.version ?? 0);

  useEffect(() => {
    // only call the watchlist API when version changes
    getWatchList();
  }, [watchlistVersion]);

  useEffect(() => {
    getTradeIdeas(tradeType);
  }, [tradeType, showOnlyLoanEligible]);

  useEffect(() => {
    if (marketSignalOpt) {
      getMarketSignalsFilterData();
    }
  }, [marketSignalOpt]);

  useEffect(() => {
    getMyPickList();
  }, [myPicsOpt, myPicsType]);

  useEffect(() => {
    getMarketMovers();
  }, [marketMoversType]);

  // useEffect(() => {
  //   const filteredData =
  //     marketMovers?.length &&
  //     marketMovers.filter((item) => item.key === marketMoversFilter);
  //   setMarketMoversData(filteredData?.[0]?.data ?? []);
  // }, [marketMoversFilter, marketMovers]);

  // useEffect(() => {
  //   const data =
  //     allMyPickData.length &&
  //     allMyPickData.filter(
  //       (item: any) => item.recommedationType === myPicsPreference
  //     );
  //   setMyPickData(data ? data : []);
  // }, [allMyPickData, myPicsPreference]);


  const getLogoUrl = (symbol) => {
    if (!symbol) {
      throw new Error("Symbol is required");
    }
    return `https://logos-api.extend.apexclearing.com/logos/${symbol
      .toLowerCase()
      .trim()}.png?api_key=4RJeGywzAE&api_secret=isUE1ZzPNi5IzXbs9Qg0S01eaFUcxX09c4U88R9JBiJgJ6WxXHrXz3VtEima`;
  };

  const getUrlRange = (selection): string => {
    const now = new Date(new Date().setDate(new Date().getDate() - 1));
    let endPoint = "";

    const formatDate = (date: Date) =>
      date.toISOString().split("T")[0]; // Format as yyyy-MM-dd

    const adjustDate = (date: Date, adjust: { days?: number; months?: number; years?: number }) => {
      if (adjust.days) date.setDate(date.getDate() + adjust.days);
      if (adjust.months) date.setMonth(date.getMonth() + adjust.months);
      if (adjust.years) date.setFullYear(date.getFullYear() + adjust.years);
      return date;
    };
    switch (selection) {
      case "1 Day":
        endPoint = `/range/15/minute/${formatDate(adjustDate(new Date(now), { days: -1 }))}/${formatDate(now)}`;
        break;
      case "5 Day":
        endPoint = `/range/1/day/${formatDate(adjustDate(new Date(now), { days: -8 }))}/${formatDate(now)}`;
        break;
      case "1 Month":
        endPoint = `/range/1/day/${formatDate(adjustDate(new Date(now), { months: -1 }))}/${formatDate(now)}`;
        break;
      case "6 Month":
        endPoint = `/range/1/week/${formatDate(adjustDate(new Date(now), { months: -6 }))}/${formatDate(now)}`;
        break;
      case "1 Year":
        endPoint = `/range/1/month/${formatDate(adjustDate(new Date(now), { years: -1 }))}/${formatDate(now)}`;
        break;
      case "5 Years":
        endPoint = `/range/1/month/${formatDate(adjustDate(new Date(now), { years: -5 }))}/${formatDate(now)}`;
        break;
      case "All":
        endPoint = `/range/1/month/1980-01-01/${formatDate(now)}`;
        break;
      default:
        endPoint = `/range/1/day/${formatDate(adjustDate(new Date(now), { days: -1 }))}/${formatDate(now)}`;
    }
    return endPoint;
  };

  const getChartData = async (symbol: string, type: 'stock' | 'crypto' = tradeType) => {
    dispatch(startLoading());
    setChartLoading(true);

    try {
      if (type === 'stock') {
        const url = `https://api.polygon.io/v2/aggs/ticker/${symbol}${getUrlRange("1 Month")}?apiKey=${BAR_CHART_KEY}&sort=asc&limit=5000`;
        const response = await axios.get(url);
        const apiResponse = response.data.results;
        const chartData = apiResponse?.length && apiResponse.map((item: any) => ({
          x: new Date(item.t),
          y: item.c
        }));
        return chartData || [];
      } else {
        // Convert crypto symbol to SFOX pair format (e.g., BTC -> btcusd)
        const pair = `${symbol.toLowerCase()}usd`;

        // Get time range for 1 month of data
        const now = Math.floor(Date.now() / 1000);
        const startTime = now - (30 * 24 * 60 * 60); // 30 days ago
        const endTime = now;

        // Call SFOX API
        const wolfpackUrl = UrlConstant.Crypto.Candlesticks;
        const params = {
          pair,
          startTime,
          endTime,
          period: 86400, // 1 day period
          currency: currencyInfo.effectiveCurrency
        };

        const res = await Axios.get(wolfpackUrl, { params });
        const apiData = res.data?.data || [];

        const chartData = apiData.map((item: any) => ({
          x: new Date(item.start_time * 1000), // Convert seconds to milliseconds
          y: parseFloat(item.close_price)
        }));

        return chartData || [];
      }
    } catch (error) {
      console.error("Error fetching chart data:", error);
      return [];
    } finally {
      setChartLoading(false);
      dispatch(endLoading());
    }
  };

  useEffect(() => {
    if (tradeIdeas?.bullish?.Ticker) {
      getChartData(tradeIdeas.bullish.Ticker, tradeType).then((bullishChartData) => {
        setTradeIdeaBullishChartData(bullishChartData || []);
      });
    } else {
      setTradeIdeaBullishChartData([]);
    }

    if (tradeIdeas?.bearish?.Ticker) {
      getChartData(tradeIdeas.bearish.Ticker, tradeType).then((bearishChartData) => {
        setTradeIdeasBearishChartData(bearishChartData || []);
      });
    } else {
      setTradeIdeasBearishChartData([]);
    }
  }, [tradeIdeas, tradeType]);

  // Handler for starting KYC (placeholder)
  const handleStartKYC = () => {
    // TODO: Integrate Sumsub SDK or redirect to KYC flow
    alert("KYC process started (placeholder)");
  };

  const filteredWatchList = watchList.filter(item => {
    if (showOnlyLoanEligible) return item.isLoanAvailable;
    if (watchlistFilter === 'all') return true;
    if (watchlistFilter === 'stock') return item.type === 'stock' || !item.type;
    if (watchlistFilter === 'crypto') return item.type === 'crypto';
    return false;
  });

  return (
    <>
      <OnboardingStatusBar />
      <Container
        maxWidth={false}
        className="bg-color"
        style={{
          padding: " 20px 30px",
        }}
      >
        {/* ...................................................Market Trackers .......................................................... */}
        <Box
          display="flex"
          mb="50px"
          alignItems="center"
          height="fit-content"
          width="100%"
        >
          <Box width="100%">
            <Box
              display="flex"
              width="95%"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box display="flex" gap={2} alignItems="center" mb={1}>
                <Typography
                  className="text-white"
                  mb={0}
                  style={{ fontSize: "1.5rem" }}
                >
                  Market Trackers
                </Typography>


              </Box>
              {/* <Link
                  to="/mypicks"
                  style={{
                    color: "#4A7AF2",
                    textDecoration: "underline !importan",
                    textDecorationLine: "underline !important",
                  }}
                >
                  view all
                </Link> */}
            </Box>

            <Box width='100%' display='flex'>

              <Box

                display="flex"
                // justifyContent='space-around'
                gap="10px"
                marginRight="10px"
                width={"100%"}


              >
                {marketTrackersLoading
                  ? // <CircularProgress />
                  // <LoadingCards/>
                  Array.from({ length: 3 }).map((_, index) => (
                    <ThemeProvider theme={darkTheme}>
                      <Card
                        className="card"
                        sx={{ width: "450px", padding: '0px', height: 'fit-content' }}
                      >
                        <CardContent
                          className="text-white"
                          style={{
                            display: "flex",
                            alignItems: 'start',
                            justifyContent: "space-between",
                            height: "100%",
                            padding: '14px 20px'
                          }}
                        >
                          <Box>
                            <Typography className="text-white stock-label" sx={{ maxWidth: '250px' }}>
                              <Skeleton width='250px' height='20px' />
                            </Typography>
                            <Typography className="text-white" >
                              <Skeleton width='250px' height='20px' />
                            </Typography>
                          </Box>
                          <Skeleton sx={{ borderRadius: '30px' }} width='110px' height='80px' />

                        </CardContent>
                      </Card>
                    </ThemeProvider>
                  ))
                  : marketTrackers.length === 0 && (
                    <Card className="stock card">
                      <CardContent className="text-white">
                        <div
                          style={{
                            width: "100%",
                            color: "red",
                            textAlign: "center",
                          }}
                        >
                          No Data
                        </div>
                      </CardContent>
                    </Card>
                  )}
                {marketTrackers.length
                  ? marketTrackers.map((item) => {
                    return (
                      <Card
                        className="card"
                        sx={{ width: "450px", padding: '0px', height: 'fit-content' }}
                      // onClick={() => {
                      //   navigate("/stockdetail", {
                      //     state: {
                      //       symbol: item.symbol,
                      //       icon:
                      //         item.branding?.logo_url ??
                      //         item.branding?.icon_url,
                      //       title: "Market Trackers",
                      //     },
                      //   });
                      // }}
                      >
                        <CardContent
                          className="text-white"
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            height: "100%",
                            padding: '14px 20px'
                          }}
                        >
                          <Box>
                            <Typography className="text-white stock-label" sx={{ maxWidth: '250px' }}>
                              {item.stockInfo?.displayName}
                            </Typography>
                            <Box display="flex" alignItems="center">
                              <Typography className="text-white" >
                                {formatter.price(item.price).display}
                              </Typography>
                              <Typography sx={{ color: item.session.change == 0 ? '' : item.session.change > 0 ? '#4edf6d' : '#FF2A2A', }}
                              >
                                {/* ${item.price} */}
                                {formatter.display(Math.abs(item.session.change), { showSign: true })}
                                ({formatPercentage(item.session.change_percent, { showSign: true })})
                              </Typography>
                            </Box>
                          </Box>
                          <Box>
                            <Button
                              fullWidth
                              className="btn-blue"
                              sx={{ padding: '9px 36px', borderRadius: '50px', textTransform: 'none' }}
                              onClick={() => {
                                navigate("/stockdetail", {
                                  state: {
                                    symbol: item.ticker,
                                    icon:
                                      item.branding?.icon_url ??
                                      item.branding?.logo_url,
                                    title: "Market Trackers",
                                  },
                                });
                              }}
                            >
                              Info
                            </Button>

                          </Box>

                          {/* </Box> */}

                        </CardContent>
                      </Card>
                    );
                  })
                  : null}
              </Box>

            </Box>
          </Box>
        </Box>
        {/* -----------------------------------------------------------------------------Trade Ideas ------------------------------------------ */}
        {!tradeIdeaLoading &&
          !tradeIdeas?.bullish?.Ticker &&
          !tradeIdeas?.bearish?.Ticker &&
          !tradeIdeasError ? null : (
          <>
            {/* Title and Toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'left', mb: 2, gap: 2 }}>
              <Typography
                className="text-white fs-24-desktop"
                fontSize='24px'
                mb={0}
              >
                Trade Ideas
              </Typography>

              <ToggleButtonGroup
                value={tradeType}
                exclusive
                onChange={(_, value) => value && setTradeType(value)}
                sx={{
                  background: '#23272F',
                  borderRadius: '12px',
                  p: 0.5,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                  '& .MuiToggleButton-root': {
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '1rem',
                    border: 0,
                    borderRadius: '10px !important',
                    px: 3,
                    py: 1,
                    transition: 'background 0.2s',
                  },
                  '& .Mui-selected': {
                    background: '#4A7AF2',
                    color: 'white',
                  },
                }}
              >
                <ToggleButton value="crypto">Crypto</ToggleButton>
                <ToggleButton value="stock">Stock/ETF</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Error State */}
            {tradeIdeasError && !tradeIdeaLoading ? (
              <Box
                display='flex'
                justifyContent='center'
                alignItems='center'
                minHeight='200px'
                mb='50px'
              >
                <Box
                  textAlign='center'
                  padding='40px'
                  borderRadius='20px'
                  border='1px solid #FF6B6B'
                  bgcolor='#FF6B6B1A'
                >
                  <Typography
                    fontSize='20px'
                    className='text-white'
                    fontWeight='bold'
                    mb={1}
                  >
                    No Trade Ideas Available
                  </Typography>
                  <Typography
                    fontSize='16px'
                    className='text-secondary'
                  >
                    {tradeIdeasError}
                  </Typography>
                </Box>
              </Box>
            ) : null}

            {/* Trade Ideas Cards */}
            {!tradeIdeasError ? (
              <Box display='flex' mb='50px' gap='10px' alignItems='stretch'>
                {tradeIdeaLoading ? (
                  <LoadingMiniCharts />
                ) : (
                  <>
                    {/* Only show cards if we have data for current trade type */}
                    {tradeIdeas?.bullish?.Ticker && (
                      <Box width='50%' display='flex'>
                        <Box
                          width='100%'
                          display='flex'
                          borderRadius='20px'
                          border='1px solid #4EDF6D'
                          bgcolor='#4EDF6D1A'
                          padding='20px'
                        >
                          <Box width='calc(100% - 232px)' display='flex' flexDirection='column'>
                            <Box display='flex' alignItems='start' gap='20px' mb={1}>
                              <Avatar
                                sx={{
                                  height: '64px',
                                  width: '64px',
                                  minWidth: '64px',
                                  backgroundColor: "white",
                                  '& .MuiAvatar-img': {
                                    objectFit: 'contain'
                                  }
                                }}
                                src={
                                  tradeIdeas?.bullish?.summary.branding?.icon_url ??
                                  tradeIdeas?.bullish?.summary.branding?.logo_url
                                }
                              />
                              <Box flex={1} minWidth={0}>
                                <Typography
                                  fontSize='24px'
                                  fontWeight='bold'
                                  className="text-white stock-label"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    width: '100%'
                                  }}
                                  title={tradeIdeas?.bullish?.summary?.name}
                                >
                                  {tradeIdeas?.bullish?.summary?.name}
                                  {tradeIdeas?.bullish?.isLoanAvailable && (
                                    <Tooltip title="Booster Loan Eligible" arrow placement="top">
                                      <span style={{ marginLeft: '5px' }}>
                                        <ThunderIconCirlcle width={20} height={20} />
                                      </span>
                                    </Tooltip>
                                  )}
                                </Typography>
                                <Typography fontSize='18px' className="text-secondary fs-18-desktop">
                                  {tradeType === 'stock' ? 'NYSE' : 'CRYPTO'}: {tradeIdeas?.bullish?.Ticker}
                                </Typography>
                                <Box display='flex' flexDirection='column' gap='2px'>
                                  <Typography
                                    fontSize='18px'
                                    className="text-white fs-18-desktop"
                                    fontWeight='bold'
                                  >
                                    {formatter.price(tradeIdeas?.bullish?.summary?.price).display}
                                  </Typography>
                                  {tradeIdeas?.bullish?.snapshot.session.change > 0 ? (
                                    <Typography
                                      style={{ color: '#4EDF6D' }}
                                      fontSize='16px'
                                    >
                                      {formatter.display(
                                        tradeIdeas?.bullish?.snapshot?.session?.change ?? 0,
                                        { showSign: true }
                                      )}
                                      ({formatPercentage(tradeIdeas?.bullish?.snapshot?.session?.change_percent, { showSign: true })})                                    </Typography>
                                  ) : null}
                                </Box>
                                <Typography fontSize="15px" className="text-white">
                                  {moment(tradeIdeas?.bullish?.time_stamp).utcOffset(-5).format("MMM DD, YYYY")} |
                                  <span style={{ color: '#4A7AF2' }}>
                                    {' '}{moment(tradeIdeas?.bullish?.time_stamp).utcOffset(-5).format("hh:mm A")} EST
                                  </span>
                                </Typography>
                              </Box>
                            </Box>
                            <Box padding={"5px"} flex={1}>
                              <LinechartMini data={tradeIdeaBullishChartData || []} lineColor="#4EDF6D" />
                            </Box>
                          </Box>
                          <Box width='212px' minWidth='212px' ml={2}>
                            <Box mb='20px'>
                              {tradeType === 'crypto' ? (
                                <>
                                  <Box display='flex' justifyContent='space-between' mb='4px'>
                                    <Typography fontSize='14px' className='text-white'>Bid</Typography>
                                    <Typography
                                      fontSize='16px'
                                      className='text-white'
                                      fontWeight='bold'
                                      title={!!tradeIdeas?.bullish?.snapshot?.last_quote?.bid ? formatter.display(tradeIdeas?.bullish?.snapshot?.last_quote?.bid) : 'N/A'}
                                    >
                                      {!!tradeIdeas?.bullish?.snapshot?.last_quote?.bid ? formatter.display(tradeIdeas?.bullish?.snapshot?.last_quote?.bid) : 'N/A'}                                    </Typography>
                                  </Box>
                                  <Box display='flex' justifyContent='space-between' mb='4px'>
                                    <Typography fontSize='14px' className='text-white'>Ask</Typography>
                                    <Typography
                                      fontSize='16px'
                                      className='text-white'
                                      fontWeight='bold'
                                      title={!!tradeIdeas?.bullish?.snapshot?.last_quote?.ask ? formatter.display(tradeIdeas?.bullish?.snapshot?.last_quote?.ask) : 'N/A'}
                                    >
                                      {!!tradeIdeas?.bullish?.snapshot?.last_quote?.ask ? formatter.display(tradeIdeas?.bullish?.snapshot?.last_quote?.ask) : 'N/A'}
                                    </Typography>
                                  </Box>
                                  <Box display='flex' justifyContent='space-between' mb='4px'>
                                    <Typography fontSize='14px' className='text-white'>Performance (Week)</Typography>
                                    <Typography fontSize='16px' className='text-white' fontWeight='bold'>
                                      {tradeIdeas?.bullish?.["Performance (Week)"] ?? '-'}
                                    </Typography>
                                  </Box>
                                  <Box display='flex' justifyContent='space-between' mb='4px'>
                                    <Typography fontSize='14px' className='text-white'>Performance (Month)</Typography>
                                    <Typography fontSize='16px' className='text-white' fontWeight='bold'>
                                      {tradeIdeas?.bullish?.["Performance (Month)"] ?? '-'}
                                    </Typography>
                                  </Box>
                                  <Box display='flex' justifyContent='space-between' mb='4px'>
                                    <Typography fontSize='14px' className='text-white'>Performance (Quarter)</Typography>
                                    <Typography fontSize='16px' className='text-white' fontWeight='bold'>
                                      {tradeIdeas?.bullish?.["Performance (Quarter)"] ?? '-'}
                                    </Typography>
                                  </Box>
                                  <Box display='flex' justifyContent='space-between' mb='4px'>
                                    <Typography fontSize='14px' className='text-white'>Performance (Half Year)</Typography>
                                    <Typography fontSize='16px' className='text-white' fontWeight='bold'>
                                      {tradeIdeas?.bullish?.["Performance (Half Year)"] ?? '-'}
                                    </Typography>
                                  </Box>
                                  <Box display='flex' justifyContent='space-between'>
                                    <Typography fontSize='14px' className='text-white'>Volume</Typography>
                                    <Typography
                                      fontSize='16px'
                                      className='text-white'
                                      fontWeight='bold'
                                      title={formatter.display(tradeIdeas.bullish.Volume, { compact: true, showSymbol: false })}
                                    >
                                      {formatter.display(tradeIdeas.bullish.Volume, { compact: true, showSymbol: false })}
                                    </Typography>
                                  </Box>
                                </>
                              ) : (
                                <>
                                  <Box display='flex' justifyContent='space-between' mb='4px'>
                                    <Typography fontSize='14px' className='text-white'>Previous Close</Typography>
                                    <Typography
                                      fontSize='16px'
                                      className='text-white'
                                      fontWeight='bold'
                                      title={formatter.price(tradeIdeas?.bullish?.snapshot?.session?.previous_close).display}
                                    >
                                      {formatter.price(tradeIdeas?.bullish?.snapshot?.session?.previous_close).display}
                                    </Typography>
                                  </Box>
                                  <Box display='flex' justifyContent='space-between' mb='4px'>
                                    <Typography fontSize='14px' className='text-white'>Open</Typography>
                                    <Typography
                                      fontSize='16px'
                                      className='text-white'
                                      fontWeight='bold'
                                      title={formatter.price(tradeIdeas?.bullish?.snapshot?.session?.open).display}
                                    >
                                      {formatter.price(tradeIdeas?.bullish?.snapshot?.session?.open).display}
                                    </Typography>
                                  </Box>
                                  <Box display='flex' justifyContent='space-between' mb='4px'>
                                    <Typography fontSize='14px' className='text-white'>Bid</Typography>
                                    <Typography
                                      fontSize='16px'
                                      className='text-white'
                                      fontWeight='bold'
                                      title={tradeIdeas?.bullish?.snapshot?.last_quote?.bid != null ? formatter.display(tradeIdeas?.bullish?.snapshot?.last_quote?.bid) : 'N/A'}
                                    >
                                      {tradeIdeas?.bullish?.snapshot?.last_quote?.bid != null ? formatter.display(tradeIdeas?.bullish?.snapshot?.last_quote?.bid) : 'N/A'}                                    </Typography>
                                  </Box>
                                  <Box display='flex' justifyContent='space-between' mb='4px'>
                                    <Typography fontSize='14px' className='text-white'>Ask</Typography>
                                    <Typography
                                      fontSize='16px'
                                      className='text-white'
                                      fontWeight='bold'
                                      title={tradeIdeas?.bullish?.snapshot?.last_quote?.ask != null ? formatter.display(tradeIdeas?.bullish?.snapshot?.last_quote?.ask) : 'N/A'}
                                    >
                                      {tradeIdeas?.bullish?.snapshot?.last_quote?.ask != null ? formatter.display(tradeIdeas?.bullish?.snapshot?.last_quote?.ask) : 'N/A'}
                                    </Typography>
                                  </Box>
                                  <Box display='flex' justifyContent='space-between' mb='4px'>
                                    <Typography fontSize='14px' className='text-white'>High</Typography>
                                    <Typography
                                      fontSize='16px'
                                      className='text-white'
                                      fontWeight='bold'
                                      title={formatter.price(tradeIdeas?.bullish?.snapshot?.session?.high).display}
                                    >
                                      {formatter.price(tradeIdeas?.bullish?.snapshot?.session?.high).display}
                                    </Typography>
                                  </Box>
                                  <Box display='flex' justifyContent='space-between' mb='4px'>
                                    <Typography fontSize='14px' className='text-white'>Low</Typography>
                                    <Typography
                                      fontSize='16px'
                                      className='text-white'
                                      fontWeight='bold'
                                      title={formatter.price(tradeIdeas?.bullish?.snapshot?.session?.low).display}
                                    >
                                      {formatter.price(tradeIdeas?.bullish?.snapshot?.session?.low).display}
                                    </Typography>
                                  </Box>
                                  <Box display='flex' justifyContent='space-between'>
                                    <Typography fontSize='14px' className='text-white'>Volume</Typography>
                                    <Typography
                                      fontSize='16px'
                                      className='text-white'
                                      fontWeight='bold'
                                      title={formatter.display(tradeIdeas?.bullish?.Volume, { compact: true })}
                                    >
                                      {formatter.display(tradeIdeas?.bullish?.Volume, { compact: true })}
                                    </Typography>
                                  </Box>
                                </>
                              )}
                            </Box>
                            <Button
                              fullWidth
                              sx={{
                                color: 'black',
                                bgcolor: '#4EDF6D',
                                fontWeight: 'bold',
                                borderRadius: '50px',
                                textTransform: 'none',
                                mt: 'auto'
                              }}
                              onClick={() => {
                                const routePath = tradeType === 'stock' ? '/stockdetail' : '/crypto-detail';
                                navigate(routePath, {
                                  state: tradeType === 'stock' ? {
                                    symbol: tradeIdeas?.bullish?.Ticker,
                                    icon: tradeIdeas?.bullish?.summary.branding?.icon_url ??
                                      tradeIdeas?.bullish?.summary.branding?.logo_url,
                                    title: "Trade Ideas",
                                  } : {
                                    externalID: tradeIdeas?.bullish?.No,
                                    title: "Trade Ideas",
                                  }
                                });
                              }}
                            >
                              Trade
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    )}

                    {tradeIdeas?.bearish?.Ticker && (
                      <Box width='50%' display='flex'>
                        <Box
                          width='100%'
                          display='flex'
                          borderRadius='20px'
                          border='1px solid #FF2A2A'
                          bgcolor='#FF2A2A1A'
                          padding='20px'
                        >
                          <Box width='calc(100% - 232px)' display='flex' flexDirection='column'>
                            <Box display='flex' alignItems='start' gap='20px' mb={1}>
                              <Avatar
                                sx={{
                                  height: '64px',
                                  width: '64px',
                                  minWidth: '64px',
                                  backgroundColor: "white",
                                  '& .MuiAvatar-img': {
                                    objectFit: 'contain'
                                  }
                                }}
                                src={
                                  tradeIdeas?.bearish?.summary.branding?.icon_url ??
                                  tradeIdeas?.bearish?.summary.branding?.logo_url
                                }
                              />
                              <Box flex={1} minWidth={0}>
                                <Typography
                                  fontSize='24px'
                                  fontWeight='bold'
                                  className="text-white stock-label"
                                  title={tradeIdeas?.bearish?.summary?.name}
                                >
                                  {tradeIdeas?.bearish?.summary?.name}
                                  {tradeIdeas?.bearish?.isLoanAvailable && (
                                    <Tooltip title="Booster Loan Eligible" arrow placement="top">
                                      <span style={{ marginLeft: '5px' }}>
                                        <ThunderIconCirlcle width={20} height={20} />
                                      </span>
                                    </Tooltip>
                                  )}
                                </Typography>
                                <Typography fontSize='18px' className="text-secondary fs-18-desktop">
                                  {tradeType === 'stock' ? 'NYSE' : 'CRYPTO'}: {tradeIdeas?.bearish?.Ticker}
                                </Typography>
                                <Box display='flex' flexDirection='column' gap='2px'>
                                  <Typography
                                    fontSize='18px'
                                    className="text-white fs-18-desktop"
                                    fontWeight='bold'
                                  >
                                    {formatter.price(tradeIdeas?.bearish?.summary?.price).display}
                                  </Typography>
                                  {tradeIdeas?.bearish?.snapshot.session.change < 0 ? (
                                    <Typography
                                      style={{ color: "#FF2A2A" }}
                                      fontSize='16px'
                                    >
                                      {formatter.display(
                                        tradeIdeas?.bearish?.snapshot?.session?.change ?? 0,
                                        { showSign: true }
                                      )}
                                      ({formatPercentage(tradeIdeas?.bearish?.snapshot?.session?.change_percent, { showSign: true })})
                                    </Typography>
                                  ) : null}
                                </Box>
                                <Typography fontSize="15px" className="text-white">
                                  {moment(tradeIdeas?.bearish?.time_stamp).utcOffset(-5).format("MMM DD, YYYY")} |
                                  <span style={{ color: '#4A7AF2' }}>
                                    {' '}{moment(tradeIdeas?.bearish?.time_stamp).utcOffset(-5).format("hh:mm A")} EST
                                  </span>
                                </Typography>
                              </Box>
                            </Box>
                            <Box padding={"5px"} flex={1}>
                              <LinechartMini data={tradeIdeasBearishChartData || []} lineColor="#FF2A2A" />
                            </Box>
                          </Box>
                          <Box width='212px' minWidth='212px' ml={2}>

                            <Box mb='20px'>
                              {tradeType === 'crypto' ? (
                                <>
                                  <Box display='flex' justifyContent='space-between' mb='4px'>
                                    <Typography fontSize='14px' className='text-white'>Bid</Typography>
                                    <Typography
                                      fontSize='16px'
                                      className='text-white'
                                      fontWeight='bold'
                                      title={tradeIdeas?.bearish?.snapshot?.last_quote?.bid != null ? formatter.display(tradeIdeas.bearish.snapshot.last_quote.bid) : 'N/A'}
                                    >
                                      {tradeIdeas?.bearish?.snapshot?.last_quote?.bid != null ? formatter.display(tradeIdeas.bearish.snapshot.last_quote.bid) : 'N/A'}
                                    </Typography>
                                  </Box>
                                  <Box display='flex' justifyContent='space-between' mb='4px'>
                                    <Typography fontSize='14px' className='text-white'>Ask</Typography>
                                    <Typography
                                      fontSize='16px'
                                      className='text-white'
                                      fontWeight='bold'
                                      title={tradeIdeas?.bearish?.snapshot?.last_quote?.ask != null ? formatter.display(tradeIdeas.bearish.snapshot.last_quote.ask) : 'N/A'}
                                    >
                                      {tradeIdeas?.bearish?.snapshot?.last_quote?.ask != null ? formatter.display(tradeIdeas.bearish.snapshot.last_quote.ask) : 'N/A'}
                                    </Typography>
                                  </Box>
                                  <Box display='flex' justifyContent='space-between' mb='4px'>
                                    <Typography fontSize='14px' className='text-white'>Performance (Week)</Typography>
                                    <Typography fontSize='16px' className='text-white' fontWeight='bold'>
                                      {tradeIdeas?.bearish?.["Performance (Week)"] ?? '-'}
                                    </Typography>
                                  </Box>
                                  <Box display='flex' justifyContent='space-between' mb='4px'>
                                    <Typography fontSize='14px' className='text-white'>Performance (Month)</Typography>
                                    <Typography fontSize='16px' className='text-white' fontWeight='bold'>
                                      {tradeIdeas?.bearish?.["Performance (Month)"] ?? '-'}
                                    </Typography>
                                  </Box>
                                  <Box display='flex' justifyContent='space-between' mb='4px'>
                                    <Typography fontSize='14px' className='text-white'>Performance (Quarter)</Typography>
                                    <Typography fontSize='16px' className='text-white' fontWeight='bold'>
                                      {tradeIdeas?.bearish?.["Performance (Quarter)"] ?? '-'}
                                    </Typography>
                                  </Box>
                                  <Box display='flex' justifyContent='space-between' mb='4px'>
                                    <Typography fontSize='14px' className='text-white'>Performance (Half Year)</Typography>
                                    <Typography fontSize='16px' className='text-white' fontWeight='bold'>
                                      {tradeIdeas?.bearish?.["Performance (Half Year)"] ?? '-'}
                                    </Typography>
                                  </Box>
                                  <Box display='flex' justifyContent='space-between'>
                                    <Typography fontSize='14px' className='text-white'>Volume</Typography>
                                    <Typography
                                      fontSize='16px'
                                      className='text-white'
                                      fontWeight='bold'
                                      title={formatter.display(tradeIdeas.bearish.Volume, { showSymbol: false, compact: true })}
                                    >
                                      {formatter.display(tradeIdeas.bearish.Volume, { showSymbol: false, compact: true })}
                                    </Typography>
                                  </Box>
                                </>
                              ) : (
                                <>
                                  <Box display='flex' justifyContent='space-between' mb='4px'>
                                    <Typography fontSize='14px' className='text-white'>Previous Close</Typography>
                                    <Typography
                                      fontSize='16px'
                                      className='text-white'
                                      fontWeight='bold'
                                      title={tradeIdeas?.bearish?.snapshot?.session?.previous_close != null ? formatter.display(tradeIdeas.bearish.snapshot.session.previous_close) : 'N/A'}
                                    >
                                      {tradeIdeas?.bearish?.snapshot?.session?.previous_close != null ? formatter.display(tradeIdeas.bearish.snapshot.session.previous_close) : 'N/A'}
                                    </Typography>
                                  </Box>
                                  <Box display='flex' justifyContent='space-between' mb='4px'>
                                    <Typography fontSize='14px' className='text-white'>Open</Typography>
                                    <Typography
                                      fontSize='16px'
                                      className='text-white'
                                      fontWeight='bold'
                                      title={tradeIdeas?.bearish?.snapshot?.session?.open != null ? formatter.display(tradeIdeas.bearish.snapshot.session.open) : 'N/A'}
                                    >
                                      {tradeIdeas?.bearish?.snapshot?.session?.open != null ? formatter.display(tradeIdeas.bearish.snapshot.session.open) : 'N/A'}
                                    </Typography>
                                  </Box>
                                  <Box display='flex' justifyContent='space-between' mb='4px'>
                                    <Typography fontSize='14px' className='text-white'>Bid</Typography>
                                    <Typography
                                      fontSize='16px'
                                      className='text-white'
                                      fontWeight='bold'
                                      title={tradeIdeas?.bearish?.snapshot?.last_quote?.bid != null ? formatter.display(tradeIdeas.bearish.snapshot.last_quote.bid) : 'N/A'}
                                    >
                                      {tradeIdeas?.bearish?.snapshot?.last_quote?.bid != null ? formatter.display(tradeIdeas.bearish.snapshot.last_quote.bid) : 'N/A'}
                                    </Typography>
                                  </Box>
                                  <Box display='flex' justifyContent='space-between' mb='4px'>
                                    <Typography fontSize='14px' className='text-white'>Ask</Typography>
                                    <Typography
                                      fontSize='16px'
                                      className='text-white'
                                      fontWeight='bold'
                                      title={tradeIdeas?.bearish?.snapshot?.last_quote?.ask != null ? formatter.display(tradeIdeas.bearish.snapshot.last_quote.ask) : 'N/A'}
                                    >
                                      {tradeIdeas?.bearish?.snapshot?.last_quote?.ask != null ? formatter.display(tradeIdeas.bearish.snapshot.last_quote.ask) : 'N/A'}
                                    </Typography>
                                  </Box>
                                  <Box display='flex' justifyContent='space-between' mb='4px'>
                                    <Typography fontSize='14px' className='text-white'>High</Typography>
                                    <Typography
                                      fontSize='16px'
                                      className='text-white'
                                      fontWeight='bold'
                                      title={tradeIdeas?.bearish?.snapshot?.session?.high != null ? formatter.display(tradeIdeas.bearish.snapshot.session.high) : 'N/A'}
                                    >
                                      {tradeIdeas?.bearish?.snapshot?.session?.high != null ? formatter.display(tradeIdeas.bearish.snapshot.session.high) : 'N/A'}
                                    </Typography>
                                  </Box>
                                  <Box display='flex' justifyContent='space-between' mb='4px'>
                                    <Typography fontSize='14px' className='text-white'>Low</Typography>
                                    <Typography
                                      fontSize='16px'
                                      className='text-white'
                                      fontWeight='bold'
                                      title={tradeIdeas?.bearish?.snapshot?.session?.low != null ? formatter.display(tradeIdeas.bearish.snapshot.session.low) : 'N/A'}
                                    >
                                      {tradeIdeas?.bearish?.snapshot?.session?.low != null ? formatter.display(tradeIdeas.bearish.snapshot.session.low) : 'N/A'}
                                    </Typography>
                                  </Box>
                                  <Box display='flex' justifyContent='space-between'>
                                    <Typography fontSize='14px' className='text-white'>Volume</Typography>
                                    <Typography
                                      fontSize='16px'
                                      className='text-white'
                                      fontWeight='bold'
                                      title={formatter.display(tradeIdeas.bearish.Volume, { compact: true, showSymbol: false })}
                                    >
                                      {formatter.display(tradeIdeas.bearish.Volume, { compact: true, showSymbol: false })}
                                    </Typography>
                                  </Box>
                                </>
                              )}
                            </Box>
                            <Button
                              fullWidth
                              sx={{
                                color: 'black',
                                bgcolor: '#4EDF6D',
                                fontWeight: 'bold',
                                borderRadius: '50px',
                                textTransform: 'none',
                                mt: 'auto'
                              }}
                              onClick={() => {
                                const routePath = tradeType === 'stock' ? '/stockdetail' : '/crypto-detail';
                                navigate(routePath, {
                                  state: tradeType === 'stock' ? {
                                    symbol: tradeIdeas?.bearish?.Ticker,
                                    icon: tradeIdeas?.bearish?.summary.branding?.icon_url ??
                                      tradeIdeas?.bearish?.summary.branding?.logo_url,
                                    title: "Trade Ideas",
                                  } : {
                                    externalID: tradeIdeas?.bearish?.No,
                                    title: "Trade Ideas",
                                  }
                                });
                              }}
                            >
                              Trade
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    )}

                    {/* No data message when not loading and no cards */}
                    {!tradeIdeas?.bullish?.Ticker && !tradeIdeas?.bearish?.Ticker && (
                      <Box
                        width='100%'
                        display='flex'
                        justifyContent='center'
                        alignItems='center'
                        minHeight='200px'
                      >
                        <Box
                          textAlign='center'
                          padding='40px'
                          borderRadius='20px'
                          border='1px solid #4A7AF2'
                          bgcolor='#4A7AF21A'
                        >
                          <Typography
                            fontSize='20px'
                            className='text-white'
                            fontWeight='bold'
                            mb={1}
                          >
                            No {tradeType === 'stock' ? 'Stock/ETF' : 'Crypto'} Trade Ideas
                          </Typography>
                          <Typography
                            fontSize='16px'
                            className='text-secondary'
                          >
                            Check back later for new trading opportunities
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            ) : null}
          </>
        )}

        <Box style={{
          display: "flex",
          justifyContent: "space-between",
        }}>
          {/* ....................................... Left Side Content ........................................ */}
          <Box sx={{ width: "64%", left: "0", margin: "0" }}>
            {/* Crypto Section */}
            {/* <CryptoMarketSection />
            <TopStocksSection /> */}
            <MarketSection />
            {/* .......................................... Indices.................................................... */}
            {/* <Box display="flex">
            <Box mb='50px' width="100%">
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography
                  mb={2}
                  style={{ fontSize: "1.5rem", color: "#F2674A" }}
                >
                  {" "}
                  Indices{" "}
                </Typography>
                <Link
                  to="/"
                  style={{
                    color: "#4A7AF2",
                    textDecoration: "underline !importan",
                    textDecorationLine: "underline !important",
                  }}
                >
                  view all
                </Link>
              </Box>
              <Box>
                <Card className="card">
                  <CardContent className="text-white">
                    <Typography variant="body2" gutterBottom>
                      NIFTY
                    </Typography>
                    <Typography variant="body2" mb="0px" mr={10}>
                      $ 27,794.40{" "}
                      <span style={{ color: "#ff2a2a" }}>-1.35(0.01%)</span>
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            </Box>
            <IconButton sx={{ pl: "0px", marginTop: "50px" }}>
              <ExpandCircleDownOutlined
                sx={{ rotate: "-90deg" }}
              ></ExpandCircleDownOutlined>
            </IconButton>
          </Box> */}
            {/* .............................................. Watch List.......................................................... */}
            <Box
              display="flex"
              mb="50px"
              alignItems="center"
              height="fit-content"
              width="100%"
            >
              <Box width="100%">
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  width='95%'
                >
                  <Typography
                    className="text-white"
                    mb={2}
                    style={{ fontSize: "1.5rem" }}
                  >
                    Watchlist
                  </Typography>
                  <NavLink
                    to="/wachlist"
                    style={{
                      color: "#4A7AF2",
                      textDecoration: "underline !importan",
                      textDecorationLine: "underline !important",
                    }}
                  >
                    view all
                  </NavLink>
                </Box>
                <Box mb={1}>
                  <ToggleButtonGroup
                    value={watchlistFilter}
                    exclusive
                    onChange={(_, value) => value && setWatchlistFilter(value)}
                    aria-label="Watchlist Filter"
                    size="small"
                  >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="crypto">Crypto</ToggleButton>
                    <ToggleButton value="stock">Stock/ETF</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <Box width='100%' display='flex'>
                  {!watchlistLoading && filteredWatchList.length > 0 && (
                    <IconButton
                      sx={{ width: "5%", borderRadius: "0px" }}
                      onClick={handleScrollRight5}
                    >
                      <ExpandCircleDownOutlined
                        className="text-secondary"
                        sx={{ rotate: "-270deg", fill: leftarrow6 && 'white' }}
                      ></ExpandCircleDownOutlined>
                    </IconButton>
                  )}
                  <Box
                    display="flex"
                    gap="10px"
                    marginRight="10px"
                    ref={scrollRef6}
                    // justifyContent='space-around'
                    width={"100%"}

                    overflow="auto"
                    sx={{
                      "&::-webkit-scrollbar": {
                        height: "6px", // Adjust scrollbar height
                        // marginTop:'10px !important'
                        display: "none",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "rgba(255,255,255,0.1)", // Scrollbar thumb color
                        borderRadius: "4px", // Rounded scrollbar thumb
                      },
                      "&::-webkit-scrollbar-thumb:hover": {
                        backgroundColor: "", // Thumb color on hover
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "transparent", // Scrollbar track color
                      },
                    }}
                  >
                    {filteredWatchList.length > 0 ? (
                      filteredWatchList.slice(0, 7).map((item: any, index: any) => (
                        <Card
                          className="stock card dcard-hover"
                          key={index}
                          onClick={() => {
                            if (item.type === 'crypto') {
                              navigate("/crypto-detail", {
                                state: { externalID: item.externalID },
                              });
                            } else {
                              navigate("/stockdetail", {
                                state: {
                                  symbol: item.symbol,
                                  icon: item.icon,
                                  title: "Watchlist",
                                },
                              });
                            }
                          }}
                        >
                          <CardContent className="text-white">
                            <Box
                              display="flex"
                              alignItems="center"
                              gap="10px"
                              mb={1}
                            >
                              {item.icon ? (
                                <Avatar
                                  alt={item.symbol}
                                  src={item.icon}
                                  sx={{
                                    width: 30,
                                    height: 30,
                                    backgroundColor: "white",
                                    objectFit: "fill",
                                  }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    width: 30,
                                    height: 30,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: "#f0f0f0",
                                    borderRadius: "50%",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontWeight: "bold",
                                      color: "#212529",
                                      fontSize: "14px",
                                    }}
                                  >
                                    {item.symbol}
                                  </Typography>
                                </Box>
                              )}
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                className="stock-label"
                                mb={0}
                              >
                                {item.name}
                              </Typography>
                              {item.isLoanAvailable && (
                                <Tooltip title="Booster Loan Eligible" arrow placement="top">
                                  <span style={{ marginBottom: '3px' }} >
                                    <ThunderIconCirlcle
                                      width={18}
                                      height={18}
                                    />
                                  </span>
                                </Tooltip>
                              )}
                            </Box>
                            <Typography
                              variant="body1"
                              textAlign="left"
                              fontWeight={600}
                              mb="0px"
                            >
                              {formatter.price(item.price).display}
                            </Typography>
                            <Typography
                              variant="caption"
                              textAlign="left"
                              mr={2}
                              sx={{
                                color: item.changeAmount >= 0 ? "#4edf6d" : "red",
                              }}
                            >
                              {formatter.display(item.changeAmount, { showSign: true })}
                              ({formatPercentage(
                                item.changeAmount < 0
                                  ? -Math.abs(item.changePercentage)
                                  : Math.abs(item.changePercentage),
                                { showSign: true }
                              )})
                            </Typography>
                          </CardContent>
                        </Card>
                      ))
                    ) : watchlistLoading ? (
                      // <CircularProgress />
                      <LoadingCards />
                    ) : (
                      <Watchlistcard />
                    )}
                  </Box>
                  {!watchlistLoading && filteredWatchList.length > 0 && (
                    <IconButton
                      onClick={handleScrollLeft6}
                      sx={{ width: "5%", borderRadius: "0px" }}
                    >
                      <ExpandCircleDownOutlined
                        sx={{ rotate: "-90deg" }}
                      ></ExpandCircleDownOutlined>
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Box>

            {/* ............................................ Market Movers ................................................... */}
            <Box
              display="flex"
              mb="50px"
              alignItems="center"
              height="fit-content"
              width="100%"
            >
              <Box width="100%">
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width='95%'
                >
                  <Box display="flex" gap={2} alignItems="center" mb={1}>
                    <Typography
                      className="text-white"
                      mb={0}
                      style={{ fontSize: "1.5rem" }}
                    >
                      {" "}
                      Market Movers
                    </Typography>

                    <FormControl>
                      <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        value={marketMoversType}
                        className="text-white"
                        sx={{
                          // color:'!important',
                          borderRadius: "9px !important",
                          borderColor: "red !important",
                          padding: "0px !important",
                          ".MuiSvgIcon-root": {
                            color: "white !important",
                          },
                        }}
                        onChange={handleChangeMarketMovers}
                      >
                        <MenuItem value="gainers">Gainers</MenuItem>
                        <MenuItem value="losers">Losers</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <NavLink
                    to="/marketMovers"
                    style={{
                      color: "#4A7AF2",
                      textDecoration: "underline !importan",
                      textDecorationLine: "underline !important",
                    }}
                  >
                    view all
                  </NavLink>
                </Box>

                <Box mb={1}>
                  <ToggleButtonGroup
                    // className="border-white"
                    // color="primary"
                    value={marketMoversFilter}
                    exclusive
                    onChange={(event, newFilter) => {
                      if (newFilter !== null) {
                        handleChangeMarketMoversFilter(event, newFilter);
                      }
                    }}
                    aria-label="Platform"
                  >
                    <ToggleButton value="L"
                    // disabled={!hasDataFor("L")}
                    >
                      Large
                    </ToggleButton>

                    <ToggleButton value="M"
                    // disabled={!hasDataFor("M")}
                    >
                      Mid
                    </ToggleButton>

                    <ToggleButton value="S"
                    // disabled={!hasDataFor("S")}
                    >
                      Small
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <Box width='100%' display='flex'>
                  {!marketMoversLoading && marketMoversData?.length > 0 && (
                    <IconButton
                      sx={{ width: "5%", borderRadius: "0px" }}
                      onClick={handleScrollRight}
                    >
                      <ExpandCircleDownOutlined
                        className="text-secondary"
                        sx={{ rotate: "-270deg", fill: leftarrow2 && 'white' }}
                      ></ExpandCircleDownOutlined>
                    </IconButton>
                  )}
                  <Box
                    display="flex"
                    gap={"10px"}
                    marginRight="10px"
                    overflow="auto"
                    ref={scrollRef2}
                    width={"100%"}
                    sx={{
                      scrollbarWidth: "none", // For Firefox
                      "&::-webkit-scrollbar": {
                        display: "none", // For Chrome, Safari, and Edge
                      },
                    }}
                  >
                    {marketMoversLoading ? (
                      // <CircularProgress />
                      <LoadingCards />
                    ) : (
                      marketMoversData?.length === 0 && (
                        <Card className="stock card">
                          <CardContent className="text-white">
                            <div
                              style={{
                                width: "100%",
                                color: "red",
                                textAlign: "center",
                              }}
                            >
                              No Data
                            </div>
                          </CardContent>
                        </Card>
                      )
                    )}
                    {marketMoversData?.length
                      ? marketMoversData.map((item) => {
                        return (
                          <Card
                            className="stock card dcard-hover"
                            onClick={() => {
                              navigate("/stockdetail", {
                                state: {
                                  symbol: item.ticker,
                                  icon: item.branding?.icon_url ?? item.branding?.logo_url,
                                  title: "Market Movers",
                                },
                              });
                            }}
                          >
                            <CardContent className="text-white">
                              <Box
                                display="flex"
                                alignItems="center"
                                gap="10px"
                                mb={1}
                              >
                                <Avatar
                                  alt={item.ticker}
                                  src={item.branding?.icon_url ?? item.branding?.logo_url}
                                  sx={{ width: 30, height: 30 }}
                                />
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  className="stock-label"
                                  mb={0}
                                >
                                  {item?.company_name}
                                </Typography>
                              </Box>
                              <Typography
                                variant="body1"
                                fontWeight={600}
                                mb="0px"
                              >
                                {formatter.price(item.price).display}
                              </Typography>

                              <Typography
                                variant="caption"
                                mr={2}
                                sx={{ color: item.change == 0 ? '' : item.change > 0 ? '#4edf6d' : 'red', }}
                              >
                                {formatter.display(Math.abs(item.change), { showSign: true })}
                                ({formatPercentage(item.change_percent, { showSign: true })})
                              </Typography>
                            </CardContent>
                          </Card>
                        );
                      })
                      : null}

                  </Box>
                  {!marketMoversLoading && marketMoversData?.length > 0 && (
                    <IconButton
                      sx={{ width: "5%", borderRadius: "0px" }}
                      onClick={handleScrollLeft}
                    >
                      <ExpandCircleDownOutlined
                        sx={{ rotate: "-90deg" }}
                      ></ExpandCircleDownOutlined>
                    </IconButton>
                  )}
                </Box>

              </Box>
            </Box>
            {/* ................................................ Market Signals ................................................... */}
            <Box
              display="flex"
              mb="50px"
              alignItems="center"
              height="fit-content"
              width="100%"
            >
              <Box width="100%">
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width='95%'
                >
                  <Box display="flex" gap={2} alignItems="center" mb={1}>
                    <Typography
                      className="text-white"
                      mb={0}
                      style={{ fontSize: "1.5rem" }}
                    >
                      {" "}
                      Market Signals
                    </Typography>

                    <FormControl>
                      <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select2"
                        value={marketsignalselect}
                        className="text-white"
                        sx={{
                          // color:'!important',
                          borderRadius: "9px !important",
                          padding: "0px !important",
                          ".MuiSvgIcon-root": {
                            color: "white !important",
                          },
                        }}
                        onChange={handleChangeMarketSingnalsSelect}
                      >
                        {marketSignalFilers.map((item) => {

                          return (
                            <MenuItem value={item.signal} selected>
                              {item.signal}
                            </MenuItem>
                          )
                        })
                        }

                      </Select>
                    </FormControl>
                  </Box>
                  <Link
                    to="/marketSignals"
                    style={{
                      color: "#4A7AF2",
                      textDecoration: "underline !importan",
                      textDecorationLine: "underline !important",
                    }}
                  >
                    view all
                  </Link>
                </Box>

                <Box mb={1} overflow='auto' maxWidth='94%' sx={{
                  scrollbarWidth: "none", // For Firefox
                  "&::-webkit-scrollbar": {
                    display: "none", // For Chrome, Safari, and Edge
                  },
                }}>
                  <ToggleButtonGroup
                    // className="border-white"
                    // color="primary"
                    value={marketSignalOpt}
                    exclusive
                    onChange={(event, newFilter) => {
                      if (newFilter !== null) {
                        // if (marketSignalCategories?.categories?.findIndex((item) => item.name === newFilter) > 0 && !isPremium) {
                        //   showPremiumAlert(navigate)
                        // } else {
                        onMarketSignalFilterChange(event, newFilter);
                        // }
                      }
                    }}
                    aria-label="Platform"
                  >
                    {marketSignalCategories?.categories?.map((filter, i) => {
                      return (
                        <ToggleButton
                          value={filter.name}
                          sx={{ textTransform: "none" }}
                        >
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"

                          >
                            <Avatar
                              alt={filter?.name}
                              src={
                                marketSignalOpt == filter.name
                                  ? filter?.selectedIconUrl
                                  : filter?.iconUrl
                              }
                              sx={{ width: 16, height: 16, marginRight: "5px" }}
                            />
                            <Typography sx={{ textWrap: 'nowrap' }}>{filter.name || <Skeleton></Skeleton>}</Typography>
                          </Box>
                        </ToggleButton>
                      );
                    })}
                  </ToggleButtonGroup>
                </Box>
                <Box display='flex' width='100%'>
                  {!marketSignalsLoading && marketSignalFilersData?.length > 0 && (
                    <IconButton
                      sx={{ width: "5%", borderRadius: "0px" }}
                      onClick={handleScrollRight2}
                    >
                      <ExpandCircleDownOutlined
                        className="text-secondary"
                        sx={{ rotate: "-270deg", fill: leftarrow3 && 'white' }}
                      ></ExpandCircleDownOutlined>
                    </IconButton>
                  )}
                  <Box
                    display="flex"
                    gap={"10px"}
                    marginRight="10px"
                    overflow="auto"
                    ref={scrollRef3}
                    width={"100%"}
                    sx={{
                      scrollbarWidth: "none", // For Firefox
                      "&::-webkit-scrollbar": {
                        display: "none", // For Chrome, Safari, and Edge
                      },
                    }}
                  >
                    {marketSignalsLoading ? (
                      <LoadingCards />
                    ) : (
                      marketSignalFilersData.length === 0 && (
                        <Card className="stock card">
                          <CardContent className="text-white">
                            <Box
                              sx={{
                                width: "100%",
                                color: "red",
                                textAlign: "center",
                              }}
                            >
                              No Data
                            </Box>
                          </CardContent>
                        </Card>
                      )
                    )}
                    {marketSignalFilersData?.length
                      ? marketSignalFilersData.slice(0, 10).map((item) => {
                        return (
                          <Card
                            className="stock card  dcard-hover"
                            onClick={() => {
                              navigate("/stockdetail", {
                                state: {
                                  symbol: item.ticker,
                                  icon: item.branding?.icon_url ?? item.branding?.logo_url,
                                  title: "Market Signals",
                                },
                              });
                            }}
                          >
                            <CardContent className="text-white">
                              <Box
                                display="flex"
                                alignItems="center"
                                gap="10px"
                                mb={1}
                              >
                                <Avatar
                                  alt={item.ticker}
                                  src={item.branding?.icon_url ?? item.branding?.logo_url}
                                  sx={{ width: 30, height: 30 }}
                                />
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  className="stock-label"
                                  mb={0}
                                >
                                  {item.company}
                                </Typography>
                              </Box>
                              <Typography
                                variant="body1"
                                fontWeight={600}
                                mb="0px"
                              >
                                {formatter.price(item.price).display}
                              </Typography>
                              <Typography
                                variant="caption"
                                mr={2}
                                sx={{ color: item.change == 0 ? '' : item.change > 0 ? '#4edf6d' : 'red', }}
                              >
                                {formatter.display(Math.abs(item.change), { showSign: true })}
                                ({formatPercentage(item.change_percent, { showSign: true })})
                              </Typography>
                            </CardContent>
                          </Card>
                        );
                      })
                      : null}
                  </Box>
                  {!marketSignalsLoading && marketSignalFilersData?.length > 0 && (
                    <IconButton
                      sx={{ width: "5%", zIndex: "99", borderRadius: "0px" }}
                      onClick={() => {
                        handleScrollLeft3();
                      }}
                    >
                      <ExpandCircleDownOutlined
                        sx={{ rotate: "-90deg" }}
                      ></ExpandCircleDownOutlined>
                    </IconButton>
                  )}
                </Box>
              </Box>

            </Box>
            {/* ...................................................My Picks .......................................................... */}
            <Box
              display="flex"
              mb="50px"
              alignItems="center"
              height="fit-content"
              width="100%"
            >
              <Box width="100%">
                <Box
                  display="flex"
                  width="95%"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box display="flex" gap={2} alignItems="center" mb={1}>
                    <Typography
                      className="text-white"
                      mb={0}
                      style={{ fontSize: "1.5rem" }}
                    >
                      My Picks
                    </Typography>
                    <FormControl>
                      <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select2"
                        value={myPicsType}
                        className="text-white"
                        sx={{
                          // color:'!important',
                          borderRadius: "9px !important",
                          padding: "0px !important",
                          ".MuiSvgIcon-root": {
                            color: "white !important",
                          },
                        }}
                        onChange={handleChangemypicks1}
                      >
                        <MenuItem value="STOCK">Stock</MenuItem>
                        <MenuItem value="ETF" selected>
                          ETF
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select2"
                        value={myPicsPreference}
                        className="text-white"
                        sx={{
                          // color:'!important',
                          borderRadius: "9px !important",
                          padding: "0px !important",
                          ".MuiSvgIcon-root": {
                            color: "white !important",
                          },
                        }}
                        onChange={handleChangeMyPicksPreference}
                      >
                        <MenuItem value="BUY">Buy</MenuItem>
                        <MenuItem value="SELL" selected>
                          Sell
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Link
                    to="/mypicks"
                    style={{
                      color: "#4A7AF2",
                      textDecoration: "underline !importan",
                      textDecorationLine: "underline !important",
                    }}
                  >
                    view all
                  </Link>
                </Box>
                <Box mb={1}>
                  <ToggleButtonGroup
                    // className="border-white"
                    // color="primary"
                    value={myPicsOpt}
                    exclusive
                    onChange={(event, newFilter) => {
                      if (newFilter !== null) {
                        handleChangeMyPicksFilter(event, newFilter);
                      }
                    }}
                    aria-label="Platform"
                  >
                    <ToggleButton value="Live" sx={{ textTransform: "none" }}>
                      Live
                    </ToggleButton>
                    <ToggleButton value="History" sx={{ textTransform: "none" }}>
                      History
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <Box width='100%' display='flex'>
                  {!myPicsLoading && myPickData.length > 0 && (
                    <IconButton
                      sx={{ width: "5%", borderRadius: "0px" }}
                      onClick={handleScrollRight3}
                    >
                      <ExpandCircleDownOutlined
                        className="text-secondary"
                        sx={{ rotate: "-270deg", fill: leftarrow4 && 'white' }}
                      ></ExpandCircleDownOutlined>
                    </IconButton>
                  )}
                  <Box
                    ref={scrollRef4}
                    display="flex"
                    // justifyContent='space-around'
                    gap="10px"
                    marginRight="10px"
                    width={"100%"}

                    overflow="auto"
                    sx={{
                      "&::-webkit-scrollbar": {
                        height: "6px", // Adjust scrollbar height
                        // marginTop:'10px !important'
                        display: "none",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "rgba(255,255,255,0.1)", // Scrollbar thumb color
                        borderRadius: "4px", // Rounded scrollbar thumb
                      },
                      "&::-webkit-scrollbar-thumb:hover": {
                        backgroundColor: "", // Thumb color on hover
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "transparent", // Scrollbar track color
                      },
                    }}
                  >
                    {myPicsLoading
                      ? // <CircularProgress />
                      // <LoadingCards/>
                      Array.from({ length: 5 }).map((_, index) => (
                        <Card
                          className="card"
                          key={index}
                          sx={{ minWidth: "300px" }}
                        >
                          <CardContent
                            className="text-white"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "40px",
                              width: "100%",
                            }}
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              gap="10px"
                              mb={1}
                              width="100%"
                            >
                              <Box sx={{ width: 30, height: 30 }}>
                                <Skeleton variant="circular" />
                              </Box>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                mb={0}
                                sx={{
                                  overflow: "hidden",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "normal",
                                  fontWeight: "bold",
                                  width: "100%",
                                }}
                              >
                                <Skeleton />
                              </Typography>
                            </Box>
                            <Box>
                              <Typography
                                variant="body1"
                                fontWeight={600}
                                mb="0px"
                                textAlign="end"
                                width="100%"
                              >
                                <Skeleton />
                              </Typography>
                              <Typography
                                variant="caption"
                                mb={0}
                                style={{ color: "#ff2a2a" }}
                              >
                                <Skeleton />
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      ))
                      : myPickData.length === 0 && (
                        <Card className="stock card">
                          <CardContent className="text-white">
                            <div
                              style={{
                                width: "100%",
                                color: "red",
                                textAlign: "center",
                              }}
                            >
                              No Data
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    {myPickData.length
                      ? myPickData.map((item) => {
                        return (
                          <Card
                            className="card dcard-hover"
                            sx={{ minWidth: "300px" }}
                            onClick={() => {
                              navigate("/stockdetail", {
                                state: {
                                  symbol: item.symbol,
                                  icon:
                                    item.branding?.icon_url ??
                                    item.branding?.logo_url,
                                  title: "My Picks",
                                },
                              });
                            }}
                          >
                            <CardContent
                              className="text-white"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              <Box display="flex" alignItems="center" gap="10px" mb={1}>
                                {item.branding?.icon_url || item.branding?.logo_url ? (
                                  <Avatar
                                    alt={item.branding?.icon_url}
                                    src={item.branding?.icon_url ?? item.branding?.logo_url}
                                    sx={{
                                      width: 30,
                                      height: 30,
                                      backgroundColor: "white",
                                      objectFit: "fill",
                                    }}
                                  />
                                ) : (
                                  <Box
                                    sx={{
                                      width: 30,
                                      height: 30,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      backgroundColor: "#f0f0f0",
                                      borderRadius: "50%",
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontWeight: "bold",
                                        color: "#212529",
                                        fontSize: "14px",
                                      }}
                                    >
                                      {item.symbol}
                                    </Typography>
                                  </Box>
                                )}
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  mb={0}
                                  sx={{
                                    overflow: "hidden",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "normal",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {item.displayName}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography
                                  variant="body1"
                                  fontWeight={600}
                                  mb="0px"
                                  textAlign="end"
                                >
                                  {item.Last == null
                                    ? formatter.price(item.regularMarketPrice).display
                                    : formatter.price(item.Last).display}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  mb={0}
                                  style={{ color: "#ff2a2a", display: 'block' }}
                                >
                                  {item.Last == null
                                    ? `Previous close`
                                    : `Approx`}
                                </Typography>
                              </Box>
                            </CardContent>
                          </Card>
                        );
                      })
                      : null}
                  </Box>
                  {!myPicsLoading && myPickData.length > 0 && (
                    <IconButton
                      onClick={handleScrollLeft4}
                      sx={{ width: "5%", borderRadius: "0px" }}
                    >
                      <ExpandCircleDownOutlined
                        sx={{ rotate: "-90deg" }}
                      ></ExpandCircleDownOutlined>
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Box>

            {/* ............................................ AI Picks ................................................... */}
            {/* <Box
              display="flex"
              mb="50px"
              alignItems="center"
              height="fit-content"
              width="100%"
            >
              <Box width="100%">
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width='95%'
                >
                  <Box display="flex" gap={2} alignItems="center" mb={1}>
                    <Typography
                      className="text-white"
                      mb={0}
                      style={{ fontSize: "1.5rem" }}
                    >
                      AI Picks
                    </Typography>

                    <FormControl>
                      <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        value={buyOrSell}
                        className="text-white"
                        sx={{
                          borderRadius: "9px !important",
                          padding: "0px !important",
                          ".MuiSvgIcon-root": {
                            color: "white !important",
                          },
                        }}
                        onChange={handleAiPickBuyOrSell}
                      >
                        <MenuItem value={Constant.BUY}>Buy</MenuItem>
                        <MenuItem value={Constant.SELL}>Sell</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        value={stockOrETF}
                        className="text-white"
                        sx={{
                          borderRadius: "9px !important",
                          padding: "0px !important",
                          ".MuiSvgIcon-root": {
                            color: "white !important",
                          },
                        }}
                        onChange={handleStockOrETF}
                      >
                        <MenuItem value={Constant.STOCK}>Stock</MenuItem>
                        <MenuItem value={Constant.ETF}>ETF</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <NavLink
                    to="/aipicks"
                    style={{
                      color: "#4A7AF2",
                      textDecoration: "underline !important",
                      textDecorationLine: "underline !important",
                    }}
                  >
                    view all
                  </NavLink>
                </Box>

                <Box mb={1}>
                  <ToggleButtonGroup
                    value={selectedCategory}
                    exclusive
                    onChange={(event, newFilter) => {
                      if (newFilter !== null) {
                        if (Constant.POPULAR !== newFilter && !isPremium) {
                          showPremiumAlert(navigate)
                        } else {
                          handleSelectedCategory(event, newFilter);
                        }
                      }
                    }}
                    aria-label="Platform"
                  >
                    <ToggleButton
                      value={Constant.POPULAR}
                      sx={{ textTransform: "none" }}
                    >
                      Popular
                    </ToggleButton>
                    <ToggleButton
                      value={Constant.DIVIDENT_PAYING}
                      sx={{ textTransform: "none" }}
                    >
                      Dividend Paying
                    </ToggleButton>
                    <ToggleButton
                      value={Constant.GROWTH}
                      sx={{ textTransform: "none" }}
                    >
                      Growth
                    </ToggleButton>
                    <ToggleButton
                      value={Constant.FOLLOWING}
                      sx={{ textTransform: "none" }}
                    >
                      Following
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <Box width='100%' display='flex'>
                  {!aiPicksLoading && aiPicksList.length > 0 && (
                    <IconButton
                      sx={{ width: "5%", borderRadius: "0px" }}
                      onClick={handleScrollRight4}
                    >
                      <ExpandCircleDownOutlined
                        className="text-secondary"
                        sx={{ rotate: "-270deg", fill: leftarrow5 && 'white' }}
                      ></ExpandCircleDownOutlined >
                    </IconButton>
                  )}
                  <Box
                    display="flex"
                    gap="10px !important"
                    overflow="auto"
                    ref={scrollRef5}
                    sx={{
                      "::-webkit-scrollbar": {
                        display: "none",
                      },
                    }}
                  >
                    {aiPicksLoading ? (
                      // <CircularProgress />
                      <LoadingCards />
                    ) : (
                      aiPicksList.length === 0 && (
                        <Card className="stock card">
                          <CardContent className="text-white">
                            <div
                              style={{
                                width: "100%",
                                color: "red",
                                textAlign: "center",
                              }}
                            >
                              No Data
                            </div>
                          </CardContent>
                        </Card>
                      )
                    )}

                    {aiPicksList.map((item: any, index: any) => (
                      <IconButton
                        sx={{ padding: "0px" }}
                        onClick={() => {
                          navigate("/stockdetail", {
                            state: {
                              symbol: item.symbol,
                              icon:
                                item.branding?.icon_url ?? item.branding?.logo_url,
                              title: "AI Picks",
                            },
                          });
                        }}
                      >
                        <Card className="stock card dcard-hover" key={index}>
                          <CardContent
                            className="text-white"
                            sx={{ display: "flex", flexDirection: "column" }}
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              gap="10px"
                              mb={1}
                            >
                              {item.icon ? (
                                <Avatar
                                  className="stock-item."
                                  alt={item.icon}
                                  src={item.icon}
                                  sx={{
                                    width: 30,
                                    height: 30,
                                    backgroundColor: "white",
                                    objectFit: "fill",
                                  }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    width: 30,
                                    height: 30,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: "#f0f0f0",
                                    borderRadius: "50%",
                                  }}
                                >
                                  <Typography
                                    // variant="h3"
                                    sx={{
                                      fontWeight: "bold",
                                      color: "#212529",
                                      fontSize: "14px",
                                    }}
                                  >
                                    {item.symbol}
                                  </Typography>
                                </Box>
                              )}

                              <Typography
                                variant="body2"
                                fontWeight={600}
                                className="stock-label"
                                mb={0}
                              >
                                {item.displayName}
                              </Typography>
                            </Box>
                            <Typography
                              variant="body1"
                              textAlign="left"
                              fontWeight="bold"
                              mb="0px"
                            >

                              {item.stockPrice === null
                                ? "-"
                                : formatter.price(item.stockPrice).display}
                            </Typography>
                            <Typography
                              mr={2}
                              textAlign="left"
                              variant="caption"
                              sx={{
                                position: "relative",
                                color: item.Change >= 0 ? "#4edf6d" : "red",
                              }}
                            >
                              {formatter.display(item.Change, { showSign: true })}{' '}
                              ({formatPercentage(item.ChangePercentage, { showSign: true })})
                            </Typography>
                          </CardContent>
                        </Card>
                      </IconButton>
                    ))}

                  </Box>

                  {!aiPicksLoading && aiPicksList.length > 0 && (
                    <IconButton
                      onClick={handleScrollLeft5}
                      sx={{ width: "5%", borderRadius: "0px" }}
                    >
                      <ExpandCircleDownOutlined
                        sx={{ rotate: "-90deg" }}
                      ></ExpandCircleDownOutlined>
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Box> */}

            {/* ..................................................... Booster Loan .................................................... */}
            <Box
              display="flex"
              mb="50px"
              alignItems="center"
              height="fit-content"
              width="100%"
            >
              <Box width="100%">
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width="95%"
                  marginBottom={1}
                >
                  <Typography
                    className="text-white"
                    mb={0}
                    style={{ fontSize: "1.5rem" }}
                  >
                    Booster Loan
                  </Typography>
                  <NavLink
                    to="/boosterloan"
                    style={{
                      color: "#4A7AF2",
                      textDecoration: "underline !important",
                      textDecorationLine: "underline !important",
                    }}
                  >
                    view all
                  </NavLink>
                </Box>

                <Box width='100%' display='flex'>
                  {!boosterLoanLoading && boosterLoanRepaymentData.length > 0 && (
                    <IconButton
                      sx={{ width: "5%", borderRadius: "0px" }}
                      onClick={handleScrollRight7}
                    >
                      <ExpandCircleDownOutlined
                        className="text-secondary"
                        sx={{ rotate: "-270deg", fill: leftarrow7 && 'white' }}
                      ></ExpandCircleDownOutlined>
                    </IconButton>
                  )}

                  <Box
                    display="flex"
                    gap="10px !important"
                    width='100%'
                    overflow="auto"
                    ref={scrollRef7}
                    sx={{
                      "::-webkit-scrollbar": {
                        display: "none",
                      },
                    }}
                  >
                    {boosterLoanLoading ? (
                      <BoosterLoanSkeleton />
                    ) : (
                      <>
                        {/* Show BoosterLoanCard only if user hasn't opted in or doesn't have a booster loan ID */}
                        {(user?.boosterLoanOptIn !== true || !user?.boosterloan_id) && (
                          <BoosterLoanCard />
                        )}

                        {/* Show "No Data" UI if user has opted in but has no active repayments */}
                        {(user?.boosterLoanOptIn === true && user?.boosterloan_id) &&
                          boosterLoanRepaymentData.length === 0 && (
                            <Card className="stock card">
                              <CardContent className="text-white">
                                <div
                                  style={{
                                    width: "100%",
                                    color: "red",
                                    textAlign: "center",
                                  }}
                                >
                                  No Data
                                </div>
                              </CardContent>
                            </Card>
                          )}
                      </>
                    )}

                    {boosterLoanRepaymentData?.map((item, index) => {
                      return (
                        <Card
                          className="stock card dcard-hover"
                          key={index}
                          role="button"
                          tabIndex={0}
                          onClick={() => navigate('/boosterLoanDetails', { state: { symbol: item.symbol, loan_id: item.loan_id } })}
                          onKeyDown={(e) => { if (e.key === 'Enter') navigate('/boosterLoanDetails', { state: { symbol: item.symbol, loan_id: item.loan_id } }) }}
                          sx={{
                            minWidth: "300px",
                            borderRadius: '12px',
                            background: '#23242A',
                            border: '1px solid rgba(255,255,255,0.1)',
                            cursor: 'pointer',
                          }}
                        >
                          <CardContent className="text-white" sx={{ padding: '16px !important', '&:last-child': { paddingBottom: '16px !important' } }}>
                            <Box>
                              {/* Header with Logo and Symbol */}
                              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Box display="flex" alignItems="center" gap="10px">
                                  <Avatar
                                    src={item.logoUrl}
                                    alt={item.symbol}
                                    sx={{ width: 30, height: 30, backgroundColor: "white" }}
                                  />
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      fontWeight={600}
                                      mb={0}
                                      sx={{ fontSize: '16px' }}
                                    >
                                      {item.symbol}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        backgroundColor: '#4EDF6D33',
                                        color: '#4EDF6D',
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        fontWeight: 600,
                                        fontSize: '11px'
                                      }}
                                    >
                                      upcoming
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>

                              {/* Amount */}
                              <Typography
                                variant="body1"
                                textAlign="left"
                                fontWeight="bold"
                                mb="4px"
                                sx={{ fontSize: 20 }}
                              >
                                {formatter.balance(item.amount).display}
                              </Typography>

                              {/* Installment Info */}
                              <Typography fontSize='12px' mb={1.5} sx={{ opacity: 0.7, textAlign: 'left' }}>
                                {item.installment_no ? `${String(item.installment_no).padStart(2, '0')} out of ${item.total_installment ?? 10}` : '—'}
                              </Typography>

                              {/* Progress Bar */}
                              <Box mb={2}>
                                <LinearProgress
                                  variant="determinate"
                                  value={item.installment_no ? (item.installment_no / (item.total_installment ?? 10)) * 100 : 0}
                                  sx={{
                                    height: 4,
                                    borderRadius: 2,
                                    backgroundColor: 'rgba(255,255,255,0.1)',
                                    '& .MuiLinearProgress-bar': {
                                      borderRadius: 2,
                                      backgroundColor: '#4A7AF2'
                                    }
                                  }}
                                />
                              </Box>

                              {/* Due Date */}
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{
                                  padding: '12px',
                                  backgroundColor: 'rgba(255,255,255,0.05)',
                                  borderRadius: '8px',
                                  border: '1px solid rgba(255,255,255,0.1)'
                                }}
                              >
                                <Box display="flex" flexDirection="column" alignItems="flex-start">
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    mb="2px"
                                    sx={{ fontSize: '14px' }}
                                  >
                                    {getDateFromTimestamp(item.due_date)}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    mb={0}
                                    color="#4A7AF2"
                                    sx={{ fontWeight: 500 }}
                                  >
                                    {getWeekdayFromTimestamp(item.due_date)}
                                  </Typography>
                                </Box>
                                <EventNote sx={{ color: '#4A7AF2', fontSize: '20px' }} />
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>

                  {!boosterLoanLoading && boosterLoanRepaymentData.length > 0 && (
                    <IconButton
                      onClick={handleScrollLeft7}
                      sx={{ width: "5%", borderRadius: "0px" }}
                    >
                      <ExpandCircleDownOutlined
                        sx={{ rotate: "-90deg" }}
                      ></ExpandCircleDownOutlined>
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* ...................................... Right Side Content ................................... */}
          <Container
            style={{ width: "440px", height: "fit-content", paddingRight: "0px" }}
          >
            <SumSubKYC />
            <RightSideSection />
          </Container>
        </Box>
      </Container>
    </>
  );
}

export default Dashboard;
